import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import useFeatureFlags from '../hooks/useFeatureFlags';

import axios from 'axios';
import {
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  Calendar,
  User,
  Download,
  FileText,
  Settings,
  Shield,
  Activity,
  Target,
  AlertTriangle,
  RefreshCw,
  Edit,
  Plus,
  Upload,
  History,
  FileCheck,
  Map,
  Navigation,
  RotateCw,
  BarChart3,
  Lock,
  ArrowRight,
  Trash,
  Eye
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const tipoContratoDispensaGeofence = (tipoContrato) => {
  const normalizado = String(tipoContrato || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[ -]/g, '_');
  return ['home_office', 'homeoffice', 'hibrido', 'hibrida'].includes(normalizado);
};

// Funções auxiliares compartilhadas
const formatarDataHora = (dateString) => {
  return new Date(dateString).toLocaleString('pt-BR');
};

const getStatusBadge = (status) => {
  const variants = {
    'VALIDA': 'default',
    'AJUSTADA': 'secondary',
    'PENDENTE_APROVACAO': 'destructive'
  };
  
  return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
};

const getTipoMarcacaoBadge = (tipo) => {
  const variants = {
    'ENTRADA': { color: 'bg-green-100 text-green-800', label: 'Entrada' },
    'SAIDA': { color: 'bg-red-100 text-red-800', label: 'Saída' },
    'SAIDA_INTERVALO': { color: 'bg-yellow-100 text-yellow-800', label: 'Saída Intervalo' },
    'RETORNO_INTERVALO': { color: 'bg-blue-100 text-blue-800', label: 'Retorno Intervalo' }
  };
  
  const config = variants[tipo] || { color: 'bg-gray-100 text-gray-800', label: tipo };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

const TimeTrackingPage = () => {
  const { user, logout } = useAuth();
  const ALL_PAGE_TABS = ['marcar-ponto', 'historico', 'folha-ponto', 'documentos', 'relatorios', 'configuracoes'];
  const allowedTabs = ALL_PAGE_TABS;
  const visibleTabs = [...ALL_PAGE_TABS, 'ajustes', 'enviar-documento'];
  const { toast } = useToast();
  const { flags: featureFlags } = useFeatureFlags();
  const allowDeleteRegistro = featureFlags.allow_delete_timetracking !== false;
  
  // Estados principais
  const [activeTab, setActiveTab] = useState('marcar-ponto');
  const [loginRequired, setLoginRequired] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [registrandoPonto, setRegistrandoPonto] = useState(false);
  const [registrosRecentes, setRegistrosRecentes] = useState([]);
  const [registrosHoje, setRegistrosHoje] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [geofencesFuncionario, setGeofencesFuncionario] = useState([]);
  const [tipoContratoFuncionario, setTipoContratoFuncionario] = useState(null);

  // Estados para geolocalização
  const [geoStatus, setGeoStatus] = useState('idle'); // idle, loading, success, error
  const [showMapModal, setShowMapModal] = useState(false); // NOVO: Modal do mapa
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [multipleReadings, setMultipleReadings] = useState([]); // NOVO: Múltiplas leituras GPS
  const [recalibrando, setRecalibrando] = useState(false); // NOVO: Status de recalibração
  
  // Estados para webcam e selfie
  const [modalAberto, setModalAberto] = useState(false);
  const [tipoSelecionado, setTipoSelecionado] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [fotoCapturada, setFotoCapturada] = useState(null);
  const [capturandoFoto, setCapturandoFoto] = useState(false);
  
  // Estados para solicitações de ajuste
  const [minhasSolicitacoes, setMinhasSolicitacoes] = useState([]);
  const [carregandoSolicitacoes, setCarregandoSolicitacoes] = useState(false);
  const [solicitacoesPendentesHoje, setSolicitacoesPendentesHoje] = useState([]);
  const [solicitacaoDetalhes, setSolicitacaoDetalhes] = useState(null);
  
  // Estados para envio de documentos
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [meusDocumentos, setMeusDocumentos] = useState([]);
  const [documentoForm, setDocumentoForm] = useState({
    tipo_documento_id: '',
    motivo: '',
    arquivo: null
  });
  const [enviandoDocumento, setEnviandoDocumento] = useState(false);
  const [carregandoDocumentos, setCarregandoDocumentos] = useState(false);
  
  // Estados para modal de nova solicitação
  const [modalNovaSolicitacao, setModalNovaSolicitacao] = useState(false);
  const [editandoAjusteId, setEditandoAjusteId] = useState(null);
  const [novaSolicitacao, setNovaSolicitacao] = useState({
    
    tipo_solicitacao: 'ponto_esquecido',
    tipo_ponto: 'ENTRADA',
    data: '',
    data_fim: '',
    hora: '',
    hora_inicio: '',
    hora_fim: '',
    horas_compensar: '',
    justificativa_falta_id: '',
    justificativa: '',
    anexo: null
  });
  const [enviandoSolicitacao, setEnviandoSolicitacao] = useState(false);
  
  // Estados específicos para correção de registro
  const [registrosParaCorrigir, setRegistrosParaCorrigir] = useState([]);
  const [registroSelecionado, setRegistroSelecionado] = useState(null);
  const [buscandoRegistros, setBuscandoRegistros] = useState(false);


  useEffect(() => {
    // VERIFICAÇÃO CRÍTICA: Usuário deve estar logado e ter employee_id
    if (!user) {
      setLoginRequired(true);
      toast({
        title: "Login Necessário",
        description: "Faça login para acessar o registro de ponto",
        variant: "destructive"
      });
      return;
    }
    
    if (!user.employee_id) {
      toast({
        title: "Funcionário não vinculado",
        description: "Entre em contato com o administrador para vincular sua conta",
        variant: "destructive"
      });
      return;
    }
    
    // Se chegou até aqui, usuário está autenticado corretamente
    obterLocalizacao().catch(() => {
      // Erro já tratado internamente (setLocationError + setGeoStatus
      // exibem o aviso "Habilite a localização" na tela) 
    });
    carregarRegistrosRecentes();
    buscarRegistrosHoje();
    buscarSolicitacoesPendentesHoje();
    carregarGeofences();
    carregarGeofencesFuncionario();
  }, [user]);

  // Carregar solicitações quando mudar para aba de ajustes
  useEffect(() => {
    if (activeTab === 'ajustes' && user?.employee_id) {
      carregarMinhasSolicitacoes();
    }
  }, [activeTab, user]);

  // Carregar documentos quando mudar para aba de enviar documento
  useEffect(() => {
    if (activeTab === 'enviar-documento' && user?.employee_id) {
      carregarTiposDocumentos();
      carregarMeusDocumentos();
    }
  }, [activeTab, user]);

  // FUNÇÃO MELHORADA: Obter localização com múltiplas leituras
  const obterLocalizacao = (numLeituras = 1) => {
    return new Promise((resolve, reject) => {
      setGeoStatus('loading');
      
      if (!navigator.geolocation) {
        const error = 'Geolocalização não suportada pelo navegador';
        setLocationError(error);
        setGeoStatus('error');
        reject(new error(errormsg));
        return;
      }

      console.log(`🌍 Buscando ${numLeituras} leitura(s) GPS...`);
      
      const leituras = [];
      let leiturasCompletas = 0;

      const fazerLeitura = (index) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const leitura = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: new Date().toISOString()
            };
            
            leituras.push(leitura);
            leiturasCompletas++;
            
            console.log(`✅ Leitura ${leiturasCompletas}/${numLeituras}:`, leitura);
            
            if (leiturasCompletas === numLeituras) {
              const melhorLocalizacao = numLeituras > 1 
                ? calcularMediaLeituras(leituras)
                : leituras[0];
              
              setLocation(melhorLocalizacao);
              setMultipleReadings(leituras);
              setGeoStatus('success');
              setLocationError('');
              resolve(melhorLocalizacao);
            } else {
              setTimeout(() => fazerLeitura(index + 1), 500);
            }
          },
          (error) => {
            let errorMsg = 'Erro ao obter localização: ';
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMsg += 'Permissão negada. Habilite a localização no seu navegador.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMsg += 'Localização não disponível.';
                break;
              case error.TIMEOUT:
                errorMsg += 'Timeout na obtenção da localização.';
                break;
              default:
                errorMsg += 'Erro desconhecido.';
                break;
            }
            
            if (leiturasCompletas === 0) {
              setLocationError(errorMsg);
              setGeoStatus('error');
              reject(error);
            } else {
              const melhorLocalizacao = calcularMediaLeituras(leituras);
              setLocation(melhorLocalizacao);
              setGeoStatus('warning');
              resolve(melhorLocalizacao);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0 // Sem cache
          }
        );
      };

      fazerLeitura(0);
    });
  };

  // Calcular média de múltiplas leituras GPS
  const calcularMediaLeituras = (leituras) => {
    if (leituras.length === 0) return null;
    if (leituras.length === 1) return leituras[0];

    const leiturasValidas = leituras.filter(l => l.accuracy < 100);
    const leiturasParaMedia = leiturasValidas.length > 0 ? leiturasValidas : leituras;

    const lat = leiturasParaMedia.reduce((sum, l) => sum + l.latitude, 0) / leiturasParaMedia.length;
    const lng = leiturasParaMedia.reduce((sum, l) => sum + l.longitude, 0) / leiturasParaMedia.length;
    const acc = Math.min(...leiturasParaMedia.map(l => l.accuracy));

    return {
      latitude: lat,
      longitude: lng,
      accuracy: acc,
      timestamp: new Date().toISOString(),
      fromMultipleReadings: true,
      numReadings: leiturasParaMedia.length
    };
  };

  // Recalibrar GPS (3 leituras)
  const recalibrarGPS = async () => {
    try {
      setRecalibrando(true);
      toast({
        title: "🎯 Recalibrando GPS...",
        description: "Fazendo 3 leituras para maior precisão",
      });

      await obterLocalizacao(3);

      toast({
        title: "✅ GPS Recalibrado!",
        description: `Precisão: ${location?.accuracy?.toFixed(0)}m`,
      });
    } catch (error) {
      toast({
        title: "Erro ao recalibrar",
        description: error.message || "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setRecalibrando(false);
    }
  };

  const carregarRegistrosRecentes = async () => {
    if (!user?.employee_id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/timetracking/registros/${user.employee_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('📋 carregarRegistrosRecentes - Total retornado:', response.data.registros?.length || 0);
      console.log('📋 Primeiros 10:', response.data.registros?.slice(0, 10));
      
      setRegistrosRecentes(response.data.registros.slice(0, 10)); // Últimos 10 registros
    } catch (error) {
      console.error('Erro ao carregar registros:', error);
    }
  };

  const carregarGeofences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/timetracking/geofences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGeofences(response.data);
    } catch (error) {
      console.error('Erro ao carregar geofences:', error);
    }
  };

  const carregarGeofencesFuncionario = async () => {
    if (!user?.employee_id) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Buscar dados do employee para pegar suas geofences associadas
      const employeeResponse = await axios.get(`${API}/api/employees/${user.employee_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTipoContratoFuncionario(employeeResponse.data.tipo_contrato || null);
      if (tipoContratoDispensaGeofence(employeeResponse.data.tipo_contrato)) {
        console.log('✅ Geofence dispensada pela modalidade de trabalho:', employeeResponse.data.tipo_contrato);
        setGeofencesFuncionario([]);
        return;
      }
      
      // CORREÇÃO: O campo correto é 'geofence_ids' (não 'geofences_ids')
      const geofenceIds = employeeResponse.data.geofence_ids || [];
      
      if (geofenceIds.length === 0) {
        console.log('⚠️ Funcionário não tem geofences associadas');
        setGeofencesFuncionario([]);
        return;
      }
      
      // Buscar todas as geofences
      const geofencesResponse = await axios.get(`${API}/api/timetracking/geofences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrar apenas as geofences associadas ao funcionário
      const geofencesDoFuncionario = geofencesResponse.data.filter(geo => 
        geofenceIds.includes(geo.id)
      );
      
      console.log(`✅ Geofences do funcionário ${user.name}:`, geofencesDoFuncionario);
      setGeofencesFuncionario(geofencesDoFuncionario);
      
    } catch (error) {
      console.error('Erro ao carregar geofences do funcionário:', error);
      setGeofencesFuncionario([]);
    }
  };


  const carregarTiposDocumentos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/tipos-documentos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiposDocumentos(response.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de documentos:', error);
    }
  };

  const carregarMeusDocumentos = async () => {
    setCarregandoDocumentos(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/documentos/meus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeusDocumentos(response.data);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar seus documentos",
        variant: "destructive"
      });
    } finally {
      setCarregandoDocumentos(false);
    }
  };

  const handleEnviarDocumentoWeb = async () => {
    if (!documentoForm.tipo_documento_id || !documentoForm.motivo || !documentoForm.arquivo) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos e selecione um arquivo",
        variant: "destructive"
      });
      return;
    }

    setEnviandoDocumento(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', documentoForm.arquivo);
      formData.append('tipo_documento_id', documentoForm.tipo_documento_id);
      formData.append('motivo', documentoForm.motivo);

      await axios.post(`${API}/api/documentos/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: "Documento enviado!",
        description: "Seu documento foi enviado com sucesso",
      });

      // Reset form
      setDocumentoForm({
        tipo_documento_id: '',
        motivo: '',
        arquivo: null
      });
      
      // Recarregar lista
      carregarMeusDocumentos();
    } catch (error) {
      console.error('Erro ao enviar documento:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível enviar o documento",
        variant: "destructive"
      });
    } finally {
      setEnviandoDocumento(false);
    }
  };

  const handleDownloadDocumento = async (documentoId, nomeArquivo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/documentos/download/${documentoId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível baixar o documento",
        variant: "destructive"
      });
    }
  };

  const carregarMinhasSolicitacoes = async () => {
    if (!user?.employee_id) return;
    
    setCarregandoSolicitacoes(true);
    try {
      const token = localStorage.getItem('token');
      
      // Buscar pontos esquecidos
      const responsePontos = await axios.get(
        `${API}/api/timetracking/solicitacoes-ponto-esquecido?funcionario_id=${user.employee_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // NOVO: Buscar ajustes/correções
      const responseAjustes = await axios.get(
        `${API}/api/timetracking/ajustes/funcionario/${user.employee_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Combinar as solicitações (adicionar tipo para diferenciá-las)
      const pontos = responsePontos.data.map(p => ({ ...p, tipo_solicitacao: 'ponto_esquecido' }));
      const ajustes = (responseAjustes.data || []).map(a => ({ ...a, tipo_solicitacao: 'correcao' }));
      
      const todasSolicitacoes = [...pontos, ...ajustes].sort((a, b) => {
        const dataA = new Date(a.criado_em || a.created_at || 0);
        const dataB = new Date(b.criado_em || b.created_at || 0);
        return dataB - dataA; // Mais recente primeiro
      });
      
      setMinhasSolicitacoes(todasSolicitacoes);
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar suas solicitações",
        variant: "destructive"
      });
    } finally {
      setCarregandoSolicitacoes(false);
    }
  };

  // NOVO: Buscar registros do dia para correção
  const buscarRegistrosParaCorrigir = async (data) => {
    if (!data || !user?.employee_id) return;
    
    setBuscandoRegistros(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/registros/${user.employee_id}?data_inicio=${data}&data_fim=${data}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('📋 Registros encontrados para correção:', response.data);
      
      // CORREÇÃO: API retorna objeto {funcionario_id, funcionario_nome, registros: [...]}
      // Precisamos acessar response.data.registros
      const registros = Array.isArray(response.data.registros) ? response.data.registros : 
                       Array.isArray(response.data) ? response.data : [];
      setRegistrosParaCorrigir(registros);
      
      if (registros.length === 0) {
        toast({
          title: "Nenhum registro encontrado",
          description: `Não há registros de ponto em ${data}`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Erro ao buscar registros:', error);
      toast({
        title: "Erro",
        description: "Não foi possível buscar os registros. Tente novamente.",
        variant: "destructive"
      });
      setRegistrosParaCorrigir([]);
    } finally {
      setBuscandoRegistros(false);
    }
  };

  const handleEditarAjuste = (sol) => {
    setEditandoAjusteId(sol.id);
    setNovaSolicitacao({
      tipo_solicitacao: 'ponto_esquecido',
      tipo_ponto: sol.tipo_ponto,
      data: sol.data,
      data_fim: '',
      hora: sol.hora,
      hora_inicio: '',
      hora_fim: '',
      horas_compensar: '',
      justificativa_falta_id: '',
      justificativa: sol.justificativa,
      anexo: null
    });
    setModalNovaSolicitacao(true);
  };

  const handleExcluirSolicitacao = async (sol) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta solicitação?')) return;

    try {
      const token = localStorage.getItem('token');
      const isCorrecao = sol.tipo_solicitacao === 'correcao';
      const endpoint = isCorrecao
        ? `${API}/api/timetracking/ajustes/${sol.id}`
        : `${API}/api/timetracking/solicitacoes-ponto-esquecido/${sol.id}`;

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({ title: "Sucesso!", description: 'Solicitação cancelada com sucesso', variant: "default" });
      setSolicitacaoDetalhes(null);
      carregarMinhasSolicitacoes();
    } catch (error) {
      toast({ title: "Erro", description: error.response?.data?.detail || 'Erro ao cancelar solicitação', variant: "destructive" });
    }
  };

  const handleEnviarSolicitacao = async () => {
    const tipoSolicitacao = novaSolicitacao.tipo_solicitacao || 'ponto_esquecido';
    
    // Validações por tipo de solicitação
    if (tipoSolicitacao === 'ponto_esquecido') {
      if (!novaSolicitacao.data || !novaSolicitacao.hora || !novaSolicitacao.justificativa) {
        toast({
          title: "Campos obrigatórios",
          description: "Preencha todos os campos para enviar a solicitação",
          variant: "destructive"
        });
        return;
      }
    } else if (tipoSolicitacao === 'correcao') {
      if (!novaSolicitacao.data || !registroSelecionado || !novaSolicitacao.hora || !novaSolicitacao.justificativa) {
        toast({
          title: "Campos obrigatórios",
          description: "Preencha todos os campos: selecione a data, o registro a corrigir, informe a hora correta e a justificativa",
          variant: "destructive"
        });
        return;
      }
    } else if (tipoSolicitacao === 'abono_falta') {
      if (!novaSolicitacao.data || !novaSolicitacao.justificativa_falta_id || !novaSolicitacao.justificativa) {
        toast({
          title: "Campos obrigatórios",
          description: "Preencha todos os campos para enviar a solicitação",
          variant: "destructive"
        });
        return;
      }
    } else if (tipoSolicitacao === 'banco_horas') {
      if (!novaSolicitacao.data || !novaSolicitacao.horas_compensar || !novaSolicitacao.justificativa) {
        toast({
          title: "Campos obrigatórios",
          description: "Preencha todos os campos para enviar a solicitação",
          variant: "destructive"
        });
        return;
      }
    }

    if (novaSolicitacao.justificativa?.length < 10) {
      toast({
        title: "Justificativa muito curta",
        description: "A justificativa deve ter no mínimo 10 caracteres",
        variant: "destructive"
      });
      return;
    }

    setEnviandoSolicitacao(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let payload = {};

      if (tipoSolicitacao === 'ponto_esquecido') {
        endpoint = `${API}/api/timetracking/solicitar-ponto-esquecido`;
        payload = {
          funcionario_id: user.employee_id,
          tipo_ponto: novaSolicitacao.tipo_ponto,
          data: novaSolicitacao.data,
          hora: novaSolicitacao.hora,
          justificativa: novaSolicitacao.justificativa
        };
      } else if (tipoSolicitacao === 'correcao') {
        // Usar registro selecionado pelo usuário
        const dataHoraOriginal = new Date(registroSelecionado.data_hora);
        const horaOriginal = dataHoraOriginal.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        
        // Criar nova data/hora para correção
        const dataHoraNovaStr = `${novaSolicitacao.data}T${novaSolicitacao.hora}:00`;
        
        console.log('📤 Enviando correção:', {
          registro_id: registroSelecionado.id,
          hora_original: horaOriginal,
          hora_nova: novaSolicitacao.hora,
          tipo: registroSelecionado.tipo_marcacao
        });
        
        endpoint = `${API}/api/timetracking/ajustes`;
        payload = {
          registro_ponto_id: registroSelecionado.id,
          funcionario_id: user.employee_id,
          tipo_ajuste: 'ALTERACAO',  // CORRIGIDO: usar enum válido
          data_hora_nova: dataHoraNovaStr,
          justificativa: novaSolicitacao.justificativa
        };
      } else if (tipoSolicitacao === 'abono_falta') {
        let documentoAnexoId = null;
        if (novaSolicitacao.anexo) {
          const formDataAnexo = new FormData();
          formDataAnexo.append('file', novaSolicitacao.anexo);
          const uploadResponse = await axios.post(
            `${API}/api/abonos/upload-anexo`,
            formDataAnexo,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
          );
          documentoAnexoId = uploadResponse.data.id;
        }

        endpoint = `${API}/api/abonos`;
        payload = {
          funcionario_id: user.employee_id,
          tipo_abono: 'ABONO_FALTA',
          data_inicio: novaSolicitacao.data,
          data_fim: novaSolicitacao.data_fim || novaSolicitacao.data,
          hora_inicio: novaSolicitacao.hora_inicio || null,
          hora_fim: novaSolicitacao.hora_fim || null,
          justificativa_id: novaSolicitacao.justificativa_falta_id || null,
          justificativa_texto: novaSolicitacao.justificativa,
          documento_id: documentoAnexoId
        };
      } else if (tipoSolicitacao === 'banco_horas') {
        endpoint = `${API}/api/abonos`;
        payload = {
          funcionario_id: user.employee_id,
          tipo_abono: 'BANCO_HORAS',
          data_inicio: novaSolicitacao.data,
          data_fim: novaSolicitacao.data,
          hora_inicio: novaSolicitacao.hora_inicio || null,
          hora_fim: novaSolicitacao.hora_fim || null,
          justificativa_texto: novaSolicitacao.justificativa
        };
      }

      if (editandoAjusteId && tipoSolicitacao === 'ponto_esquecido') {
        endpoint = `${API}/api/timetracking/solicitacoes-ponto-esquecido/${editandoAjusteId}`;
        await axios.put(endpoint, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(endpoint, payload, { headers: { Authorization: `Bearer ${token}` } });
      }

      toast({
        title: "Solicitação enviada!",
        description: "Sua solicitação foi enviada para aprovação do RH",
      });

      // Resetar formulário e fechar modal
      setNovaSolicitacao({
        tipo_solicitacao: 'ponto_esquecido',
        tipo_ponto: 'ENTRADA',
        data: '',
        data_fim: '',
        hora: '',
        hora_inicio: '',
        hora_fim: '',
        horas_compensar: '',
        justificativa_falta_id: '',
        justificativa: '',
        anexo: null
      });
      setRegistrosParaCorrigir([]);
      setRegistroSelecionado(null);
      setEditandoAjusteId(null);
      setModalNovaSolicitacao(false);

      // Recarregar lista
      carregarMinhasSolicitacoes();
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      
      // Extrair mensagem de erro do backend
      let mensagemErro = "Não foi possível enviar a solicitação";
      if (error.response?.data?.detail) {
        // Se detail é um array (erro de validação Pydantic)
        if (Array.isArray(error.response.data.detail)) {
          mensagemErro = error.response.data.detail.map(err => err.msg).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          mensagemErro = error.response.data.detail;
        }
      }
      
      toast({
        title: "Erro",
        description: mensagemErro,
        variant: "destructive"
      });
    } finally {
      setEnviandoSolicitacao(false);
    }
  };


  const buscarRegistrosHoje = async () => {
    if (!user?.employee_id) {
      console.log('⚠️ buscarRegistrosHoje: Sem employee_id');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // IMPORTANTE: Usar data LOCAL do navegador, não UTC
      // Se o navegador está no Brasil, ele vai retornar a data correta do Brasil
      const agora = new Date();
      const ano = agora.getFullYear();
      const mes = String(agora.getMonth() + 1).padStart(2, '0');
      const dia = String(agora.getDate()).padStart(2, '0');
      const hoje = `${ano}-${mes}-${dia}`;
      
      // Para comparação, mostrar também a data em UTC
      const hojeUTC = agora.toISOString().split('T')[0];
      
      const url = `${API}/api/timetracking/registros/${user.employee_id}?data_inicio=${hoje}&data_fim=${hoje}`;
      
      console.log('🔍 Buscando registros de hoje:', {
        employee_id: user.employee_id,
        dataLocalNavegador: hoje,
        dataUTC: hojeUTC,
        horarioAtual: agora.toLocaleString('pt-BR'),
        horaLocal: `${agora.getHours()}:${agora.getMinutes()}`,
        url: url
      });
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const registros = response.data.registros || [];
      console.log('✅ Registros de hoje recebidos:', registros);
      console.log('📊 Quantidade:', registros.length);
      
      if (registros.length > 0) {
        console.log('📊 Tipos de marcação hoje:', registros.map(r => ({
          tipo: r.tipo_marcacao,
          data_hora: r.data_hora,
          id: r.id
        })));
      } else {
        console.warn('⚠️ NENHUM registro encontrado para hoje:', hoje);
        console.warn('⚠️ Isso significa que ou:');
        console.warn('   1. Não há registros de hoje');
        console.warn('   2. Os registros foram salvos com data diferente');
        console.warn('   3. Há problema de timezone entre frontend e backend');
      }
      
      setRegistrosHoje(registros);
      
      // Calcular próximo tipo COM OS NOVOS DADOS (não do estado que ainda não atualizou)
      const sequencia = ['ENTRADA', 'SAIDA_INTERVALO', 'RETORNO_INTERVALO', 'SAIDA'];
      const tiposRegistrados = registros.map(r => r.tipo_marcacao);
      const proximoCalculado = tiposRegistrados.length === 0 
        ? 'ENTRADA' 
        : sequencia.find(tipo => !tiposRegistrados.includes(tipo)) || null;
      
      console.log('🎯 Próximo tipo calculado:', {
        tiposRegistrados,
        proximoCalculado
      });
      
      return registros; // Retornar para poder usar no botão atualizar
      
    } catch (error) {
      console.error('❌ Erro ao buscar registros de hoje:', error);
      console.error('Response:', error.response?.data);
      setRegistrosHoje([]);
      return [];
    }
  };

  const buscarSolicitacoesPendentesHoje = async () => {
    if (!user?.employee_id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/solicitacoes-ponto-esquecido?funcionario_id=${user.employee_id}&status=pendente`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filtrar apenas as solicitações de hoje
      const hoje = new Date().toISOString().split('T')[0];
      const solicitacoesHoje = response.data.filter(sol => sol.data === hoje);
      
      console.log('📝 Web - Solicitações pendentes de hoje:', solicitacoesHoje);
      setSolicitacoesPendentesHoje(solicitacoesHoje);
    } catch (error) {
      console.error('Erro ao buscar solicitações pendentes:', error);
    }
  };

  const obterProximoTipoPermitido = () => {
    const sequencia = ['ENTRADA', 'SAIDA_INTERVALO', 'RETORNO_INTERVALO', 'SAIDA'];
    const tiposRegistrados = registrosHoje.map(r => r.tipo_marcacao);
    
    // Criar lista de tipos já "cobertos" (registrados OU com solicitação pendente)
    const tiposCobertos = [...tiposRegistrados];
    
    // Adicionar tipos que têm solicitação pendente
    solicitacoesPendentesHoje.forEach(sol => {
      const tipoNormalizado = sol.tipo_ponto.toUpperCase();
      if (!tiposCobertos.includes(tipoNormalizado)) {
        tiposCobertos.push(tipoNormalizado);
      }
    });
    
    console.log('🔍 obterProximoTipoPermitido:', {
      registrosHoje: registrosHoje.length,
      tiposRegistrados,
      solicitacoesPendentes: solicitacoesPendentesHoje.length,
      tiposCobertos,
      sequencia
    });
    
    // Encontrar o próximo tipo na sequência que ainda não foi registrado OU solicitado
    for (const tipo of sequencia) {
      if (!tiposCobertos.includes(tipo)) {
        console.log(`✅ Próximo tipo permitido: ${tipo}`);
        return tipo;
      }
    }
    
    console.log('✅ Todos os registros completos ou com solicitação pendente');
    return null; // Todos os registros completos
  };

  const validarGeofence = () => {
    if (tipoContratoDispensaGeofence(tipoContratoFuncionario)) {
      return true;
    }

    if (!location || !geofencesFuncionario.length) {
      console.log('⚠️ Validação geofence: sem localização ou geofences do funcionário');
      return false;
    }
    
    // Função Haversine para calcular distância
    const calcularDistancia = (lat1, lon1, lat2, lon2) => {
      const R = 6371e3; // Raio da Terra em metros
      const φ1 = lat1 * Math.PI / 180;
      const φ2 = lat2 * Math.PI / 180;
      const Δφ = (lat2 - lat1) * Math.PI / 180;
      const Δλ = (lon2 - lon1) * Math.PI / 180;

      const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
                Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return R * c; // Distância em metros
    };

    // Verificar se está dentro de alguma geofence DO FUNCIONÁRIO
    for (const geo of geofencesFuncionario) {
      const distancia = calcularDistancia(
        location.latitude,
        location.longitude,
        geo.latitude,
        geo.longitude
      );
      
      // CORREÇÃO: O campo correto é 'raio_metros' (não 'raio')
      const raio = geo.raio_metros || geo.raio || 0;
      
      console.log(`📍 Geofence "${geo.nome}": distância ${distancia.toFixed(2)}m, raio ${raio}m`);
      
      if (distancia <= raio) {
        console.log(`✅ Dentro da geofence "${geo.nome}"`);
        return true;
      }
    }
    
    console.log('❌ Fora de todas as geofences associadas');
    return false;
  };

  const abrirModal = (tipo) => {
    setTipoSelecionado(tipo);
    setModalAberto(true);
    setFotoCapturada(null);
    
    // Iniciar webcam após um pequeno delay para garantir que o modal está renderizado
    setTimeout(startCamera, 100);
  };

  const fecharModal = () => {
    pararCamera();
    setModalAberto(false);
    setTipoSelecionado(null);
    setFotoCapturada(null);
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
      
      setStream(mediaStream);
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      toast({
        title: "Erro",
        description: "Erro ao acessar câmera. Verifique as permissões.",
        variant: "destructive"
      });
    }
  };

  const pararCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const capturarFoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      toast({
        title: "Erro",
        description: "Erro ao capturar foto. Tente novamente.",
        variant: "destructive"
      });
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const fotoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setFotoCapturada(fotoDataUrl);
    
    pararCamera();
  };

  const registrarPontoComFoto = async () => {
    if (!fotoCapturada) {
      toast({
        title: "Erro",
        description: "Tire uma foto antes de continuar",
        variant: "destructive"
      });
      return;
    }

    // 🔒 TRAVA DE SEGURANÇA: Verificar se a localização foi obtida
    if (!location || !location.latitude || !location.longitude) {
      toast({
        title: "Localização Necessária",
        description: "É necessário compartilhar sua localização para registrar o ponto. Por favor, habilite a localização no seu navegador e permita o acesso.",
        variant: "destructive",
        duration: 6000
      });
      return;
    }

    if (!validarGeofence()) {
      toast({
        title: "Erro",
        description: "Você está fora da área permitida para registrar ponto",
        variant: "destructive"
      });
      return;
    }

    setRegistrandoPonto(true);

    try {
      const token = localStorage.getItem('token');
      const dados = {
        funcionario_id: user.employee_id,
        tipo_marcacao: tipoSelecionado,
        origem: 'WEB',
        latitude: location.latitude, // Agora garantido que existe devido à validação acima
        longitude: location.longitude, // Agora garantido que existe devido à validação acima
        foto_comprovante: fotoCapturada,
        dispositivo_info: {
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      };

      const response = await axios.post(`${API}/api/timetracking/registrar`, dados, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Sucesso!",
        description: `${tipoSelecionado.replace(/_/g, ' ')} registrado com sucesso!`,
        variant: "default"
      });
      
      fecharModal();
      buscarRegistrosHoje();
      buscarSolicitacoesPendentesHoje();
      carregarRegistrosRecentes();

    } catch (error) {
      console.error('Erro ao registrar ponto:', error);
      
      // Verificar se precisa alterar a senha
      if (error.response?.status === 403 && 
          error.response?.data?.detail?.includes('alterar sua senha')) {
        setShowPasswordChangeModal(true);
        setRegistrandoPonto(false);
        return;
      }
      
      let errorMsg = 'Erro ao registrar ponto';
      let errorDetails = [];
      
      // Tratar erros de validação avançada
      if (error.response?.data?.detail && typeof error.response.data.detail === 'object') {
        const detalhes = error.response.data.detail;
        
        // Usar a mensagem principal se existir
        if (detalhes.message) {
          errorMsg = detalhes.message;
        }
        
        // Coletar todos os erros
        if (detalhes.erros && Array.isArray(detalhes.erros)) {
          errorDetails = [...errorDetails, ...detalhes.erros];
        }
        
        // Coletar todas as restrições
        if (detalhes.restricoes && Array.isArray(detalhes.restricoes)) {
          errorDetails = [...errorDetails, ...detalhes.restricoes.map(r => `🚫 ${r}`)];
        }
      } else if (error.response?.data?.detail) {
        errorMsg = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Erro ao registrar ponto';
      }
      
      // Montar mensagem completa
      let fullMessage = errorMsg;
      if (errorDetails.length > 0) {
        fullMessage += '\n\nDetalhes:\n• ' + errorDetails.join('\n• ');
      }
      
      toast({
        title: "Registro não aprovado",
        description: fullMessage,
        variant: "destructive",
        duration: 8000 // 8 segundos para dar tempo de ler
      });
    } finally {
      setRegistrandoPonto(false);
    }
  };

  const registrarPonto = async (tipoMarcacao) => {
    if (!user?.employee_id) {
      toast({
        title: "Erro de Autenticação",
        description: "Funcionário não identificado. Faça login novamente.",
        variant: "destructive"
      });
      setLoginRequired(true);
      return;
    }

    // 🔒 TRAVA DE SEGURANÇA: Verificar se a localização foi obtida
    if (!location || !location.latitude || !location.longitude) {
      toast({
        title: "Localização Necessária",
        description: "É necessário compartilhar sua localização para registrar o ponto. Por favor, habilite a localização no seu navegador e permita o acesso.",
        variant: "destructive",
        duration: 6000
      });
      return;
    }

    setRegistrandoPonto(true);

    try {
      const token = localStorage.getItem('token');
      const dados = {
        funcionario_id: user.employee_id,
        tipo_marcacao: tipoMarcacao,
        origem: 'WEB',
        latitude: location.latitude, // Agora garantido que existe devido à validação acima
        longitude: location.longitude, // Agora garantido que existe devido à validação acima
        dispositivo_info: {
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      };

      const response = await axios.post(`${API}/api/timetracking/registrar`, dados, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Exibir validações avançadas se existirem
      if (response.data.validacoes_avancadas) {
        const validacoes = response.data.validacoes_avancadas;
        
        // Exibir avisos
        if (validacoes.avisos && validacoes.avisos.length > 0) {
          validacoes.avisos.forEach(aviso => {
            toast({
              title: "Aviso",
              description: aviso,
              variant: "default"
            });
          });
        }
        
        // Exibir sugestões
        if (validacoes.sugestoes && validacoes.sugestoes.length > 0) {
          validacoes.sugestoes.forEach(sugestao => {
            toast({
              title: "Sugestão",
              description: sugestao,
              variant: "default"
            });
          });
        }
        
        // Exibir restrições
        if (validacoes.restricoes && validacoes.restricoes.length > 0) {
          validacoes.restricoes.forEach(restricao => {
            toast({
              title: "Restrição",
              description: `⚠️ ${restricao}`,
              variant: "default"
            });
          });
        }
      }

      toast({
        title: "Sucesso!",
        description: `${tipoMarcacao.replace('_', ' ')} registrado com sucesso!`,
        variant: "default"
      });
      carregarRegistrosRecentes();

    } catch (error) {
      console.error('Erro ao registrar ponto:', error);
      
      // Verificar se precisa alterar a senha
      if (error.response?.status === 403 && 
          error.response?.data?.detail?.includes('alterar sua senha')) {
        setShowPasswordChangeModal(true);
        setRegistrandoPonto(false);
        return;
      }
      
      let errorMsg = 'Erro ao registrar ponto';
      let errorDetails = [];
      
      // Tratar erros de validação avançada
      if (error.response?.data?.detail && typeof error.response.data.detail === 'object') {
        const detalhes = error.response.data.detail;
        
        // Usar a mensagem principal se existir
        if (detalhes.message) {
          errorMsg = detalhes.message;
        }
        
        // Coletar todos os erros
        if (detalhes.erros && Array.isArray(detalhes.erros)) {
          errorDetails = [...errorDetails, ...detalhes.erros];
        }
        
        // Coletar todas as restrições
        if (detalhes.restricoes && Array.isArray(detalhes.restricoes)) {
          errorDetails = [...errorDetails, ...detalhes.restricoes.map(r => `🚫 ${r}`)];
        }
      } else if (error.response?.data?.detail) {
        errorMsg = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Erro ao registrar ponto';
      }
      
      // Montar mensagem completa
      let fullMessage = errorMsg;
      if (errorDetails.length > 0) {
        fullMessage += '\n\nDetalhes:\n• ' + errorDetails.join('\n• ');
      }
      
      toast({
        title: "Registro não aprovado",
        description: fullMessage,
        variant: "destructive",
        duration: 8000 // 8 segundos para dar tempo de ler
      });
    } finally {
      setRegistrandoPonto(false);
    }
  };

  // Tela de Login PWA
  const LoginPWA = () => {
    const [formData, setFormData] = useState({
      email: '',
      password: ''
    });
    const [loggingIn, setLoggingIn] = useState(false);

    const handleLogin = async (e) => {
      e.preventDefault();
      
      if (!formData.email || !formData.password) {
        toast({
          title: "Erro",
          description: "Preencha email e senha",
          variant: "destructive"
        });
        return;
      }

      try {
        setLoggingIn(true);
        
        const response = await axios.post(`${API}/api/auth/login`, {
          email: formData.email,
          password: formData.password
        });

        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          // Recarregar página para atualizar contexto
          window.location.reload();
        }

      } catch (error) {
        console.error('Erro no login:', error);
        toast({
          title: "Erro no Login",
          description: error.response?.data?.detail || "Credenciais inválidas",
          variant: "destructive"
        });
      } finally {
        setLoggingIn(false);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl font-bold">GridProfile PWA</CardTitle>
            <CardDescription>Faça login para registrar seu ponto</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu.email@empresa.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Sua senha"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    password: e.target.value
                  }))}
                  required
                />
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={loggingIn}
              >
                {loggingIn ? (
                  <>
                    <Activity className="h-4 w-4 mr-2 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Entrar
                  </>
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-gray-600">
              <p>🔒 Login necessário para registro de ponto</p>
              <p className="mt-1">Entre em contato com o RH se tiver dificuldades</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Se login é necessário, mostrar tela de login
  if (loginRequired || !user) {
    return <LoginPWA />;
  }

  // Se usuário não tem employee_id, mostrar erro
  if (!user.employee_id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-xl font-bold text-red-800">Funcionário não vinculado</CardTitle>
            <CardDescription>Sua conta não está vinculada a um funcionário</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">
              Entre em contato com o administrador para vincular sua conta a um funcionário.
            </p>
            <Button onClick={logout} variant="outline" className="w-full">
              <Shield className="h-4 w-4 mr-2" />
              Fazer Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Controle de Ponto</h1>
          <p className="text-gray-600 mt-1">Sistema REP-P - Registro Eletrônico de Ponto</p>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <Shield className="h-5 w-5 text-green-600" />
          <span className="text-sm text-green-600 font-medium whitespace-nowrap">Portaria 671/2021</span>
        </div>
      </div>

      {/* Status da Geolocalização */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MapPin className={`h-5 w-5 ${geoStatus === 'success' ? 'text-green-600' : 'text-red-600'}`} />
              <div>
                <p className="font-medium">Status da Localização</p>
                {geoStatus === 'success' && location && (
                  <p className="text-sm text-gray-600">
                    Precisão: {location.accuracy ? `${Math.round(location.accuracy)}m` : 'N/A'}
                  </p>
                )}
                {locationError && (
                  <p className="text-sm text-red-600">{locationError}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {geoStatus === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : geoStatus === 'error' ? (
                <XCircle className="h-5 w-5 text-red-600" />
              ) : (
                <Activity className="h-5 w-5 text-yellow-600 animate-pulse" />
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={obterLocalizacao}
                disabled={geoStatus === 'loading'}
              >
                {geoStatus === 'loading' ? 'Obtendo...' : 'Atualizar'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList
          className="flex w-full items-center justify-start gap-1 overflow-x-auto sm:grid sm:overflow-x-visible"
          style={{gridTemplateColumns: `repeat(${visibleTabs.length}, minmax(0, 1fr))`}}
        >
          {visibleTabs.includes('marcar-ponto') && (
            <TabsTrigger value="marcar-ponto" className="flex flex-shrink-0 items-center space-x-2">
              <Clock className="h-4 w-4" />
              <span>Marcar Ponto</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('historico') && (
            <TabsTrigger value="historico" className="flex flex-shrink-0 items-center space-x-2">
              <History className="h-4 w-4" />
              <span>Histórico</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('ajustes') && (
            <TabsTrigger value="ajustes" className="flex flex-shrink-0 items-center space-x-2">
              <Edit className="h-4 w-4" />
              <span>Ajustes</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('enviar-documento') && (
            <TabsTrigger value="enviar-documento" className="flex flex-shrink-0 items-center space-x-2">
              <Upload className="h-4 w-4" />
              <span>Enviar Doc</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('folha-ponto') && (
            <TabsTrigger value="folha-ponto" className="flex flex-shrink-0 items-center space-x-2">
              <FileText className="h-4 w-4" />
              <span>Folha Ponto</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('documentos') && (
            <TabsTrigger value="documentos" className="flex flex-shrink-0 items-center space-x-2">
              <FileCheck className="h-4 w-4" />
              <span>Documentos</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('relatorios') && (
            <TabsTrigger value="relatorios" className="flex flex-shrink-0 items-center space-x-2">
              <BarChart3 className="h-4 w-4" />
              <span>Relatórios</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('configuracoes') && (
            <TabsTrigger value="configuracoes" className="flex flex-shrink-0 items-center space-x-2">
              <Settings className="h-4 w-4" />
              <span>Config</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Aba Marcar Ponto */}
        {allowedTabs.includes('marcar-ponto') && (
        <TabsContent value="marcar-ponto">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Registrar Ponto */}
            <Card>
              <CardHeader>
                <div className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <Clock className="h-5 w-5" />
                    <span>Registrar Ponto</span>
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      toast({
                        title: "Atualizando...",
                        description: "Carregando registros de hoje",
                        variant: "default"
                      });
                      const registros = await buscarRegistrosHoje();
                      await buscarSolicitacoesPendentesHoje();
                      await carregarRegistrosRecentes();
                      toast({
                        title: "Atualizado!",
                        description: `${registros?.length || 0} registro(s) de hoje. Abra o console (F12) para ver detalhes.`,
                        variant: "default",
                        duration: 5000
                      });
                    }}
                    className="text-xs"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Atualizar
                  </Button>
                </div>
                <CardDescription>
                  Registre sua entrada, saída ou intervalos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-mono font-bold text-gray-900 mb-2">
                    {new Date().toLocaleTimeString('pt-BR')}
                  </div>
                  <div className="text-sm text-gray-600">
                    {new Date().toLocaleDateString('pt-BR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                </div>

                {/* NOVO: Status GPS */}
                {location && (
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Navigation className="h-5 w-5 text-blue-600" />
                        <span className="font-semibold text-blue-900">Status GPS</span>
                      </div>
                      <Badge className={`${
                        location.accuracy < 20 ? 'bg-green-500' :
                        location.accuracy < 50 ? 'bg-yellow-500' :
                        'bg-orange-500'
                      }`}>
                        {location.accuracy?.toFixed(0)}m
                      </Badge>
                    </div>
                    
                    <div className="text-sm text-gray-700 mb-3 space-y-1">
                      <div>📍 Lat: {location.latitude?.toFixed(6)}</div>
                      <div>📍 Lng: {location.longitude?.toFixed(6)}</div>
                      {location.fromMultipleReadings && (
                        <div className="text-green-600 font-semibold">
                          ✓ {location.numReadings} leituras (alta precisão)
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={recalibrarGPS}
                        disabled={recalibrando}
                        className="flex-1 flex items-center justify-center px-3 py-2 text-sm border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                      >
                        <Target className="h-4 w-4 mr-2" />
                        {recalibrando ? 'Recalibrando...' : 'Recalibrar'}
                      </button>
                      
                      <button
                        onClick={() => setShowMapModal(true)}
                        className="flex-1 flex items-center justify-center px-3 py-2 text-sm border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <Map className="h-4 w-4 mr-2" />
                        Ver Mapa
                      </button>
                    </div>
                  </div>
                )}

                {geoStatus !== 'success' ? (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm text-yellow-800">
                        Localização necessária para registrar ponto
                      </span>
                    </div>
                  </div>
                ) : !validarGeofence() ? (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <XCircle className="h-4 w-4 text-red-600" />
                      <span className="text-sm text-red-800">
                        ⚠️ Você precisa estar dentro de uma área autorizada para registrar ponto
                      </span>
                    </div>
                  </div>
                ) : (() => {
                  const proximoTipo = obterProximoTipoPermitido();
                  
                  if (!proximoTipo) {
                    return (
                      <div className="p-6 bg-green-50 border border-green-200 rounded-lg text-center">
                        <CheckCircle className="h-12 w-12 mx-auto text-green-600 mb-3" />
                        <h3 className="text-lg font-semibold text-green-800">Registros Completos!</h3>
                        <p className="text-sm text-green-700 mt-2">
                          Você já completou todos os registros de hoje. Bom descanso! 😊
                        </p>
                      </div>
                    );
                  }

                  const botaoConfig = {
                    'ENTRADA': { label: 'Entrada', color: 'bg-green-600 hover:bg-green-700' },
                    'SAIDA_INTERVALO': { label: 'Saída Intervalo', color: 'bg-yellow-600 hover:bg-yellow-700' },
                    'RETORNO_INTERVALO': { label: 'Retorno Intervalo', color: 'bg-blue-600 hover:bg-blue-700' },
                    'SAIDA': { label: 'Saída', color: 'bg-red-600 hover:bg-red-700' }
                  }[proximoTipo];

                  return (
                    <div className="space-y-3">
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800 text-center">
                          📍 Próximo registro permitido:
                        </p>
                      </div>
                      <Button
                        onClick={() => abrirModal(proximoTipo)}
                        disabled={registrandoPonto}
                        className={`w-full ${botaoConfig.color} text-white py-6 text-lg`}
                      >
                        {registrandoPonto ? 'Registrando...' : botaoConfig.label}
                      </Button>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Registros Recentes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Activity className="h-5 w-5" />
                  <span>Registros Recentes</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {registrosRecentes.length > 0 ? (
                    registrosRecentes.map((registro) => (
                      <div key={registro.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          {/* Selfie - foto_url (disco) ou foto_comprovante (base64) */}
                          {registro.foto_url || registro.foto_comprovante ? (
                            <img 
                              src={registro.foto_url ? `${API}${registro.foto_url}` : registro.foto_comprovante}
                              alt="Selfie" 
                              className="w-12 h-12 rounded-full object-cover border-2 border-gray-300"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                          
                          <div className="text-sm">
                            <div className="font-medium">{formatarDataHora(registro.data_hora)}</div>
                            <div className="text-gray-600">{getTipoMarcacaoBadge(registro.tipo_marcacao)}</div>
                            <div className="text-xs text-gray-500">
                              {registro.origem || 'N/A'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {registro.geofence_valida ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {getStatusBadge(registro.status)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>Nenhum registro encontrado</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        )}

        {/* Aba Histórico */}
        {allowedTabs.includes('historico') && (
        <TabsContent value="historico">
          <HistoricoRegistros 
            funcionarioId={user?.employee_id}
            onSolicitarAjuste={carregarRegistrosRecentes}
          />
        </TabsContent>
        )}


        {/* Aba Ajustes */}
        <TabsContent value="ajustes">
          <Card>
            <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Edit className="h-5 w-5" />
                    Minhas Solicitações de Ajuste
                  </CardTitle>
                  <CardDescription className="text-purple-100">
                    Acompanhe suas solicitações de ajuste de ponto esquecido
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setModalNovaSolicitacao(true)}
                  className="bg-white text-purple-600 hover:bg-purple-50"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Solicitação
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {carregandoSolicitacoes ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-8 w-8 mx-auto animate-spin text-purple-600" />
                  <p className="text-gray-600 mt-2">Carregando...</p>
                </div>
              ) : minhasSolicitacoes.length === 0 ? (
                <div className="text-center py-12">
                  <Edit className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600 text-lg mb-2">Nenhuma solicitação encontrada</p>
                  <p className="text-gray-500 text-sm mb-4">
                    Clique em "Nova Solicitação" para solicitar ajustes quando esquecer de registrar um ponto
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {minhasSolicitacoes.map((sol) => {
                    // Adaptar campos dependendo do tipo de solicitação
                    const isCorrecao = sol.tipo_solicitacao === 'correcao';
                    const status = isCorrecao ? sol.status_aprovacao?.toLowerCase() : sol.status;
                    
                    // Extrair informações de data/hora
                    let dataFormatada = '';
                    let horaInfo = '';
                    let tipoTexto = '';
                    
                    if (isCorrecao) {
                      // Ajuste/Correção
                      const dataHoraNova = new Date(sol.data_hora_nova);
                      dataFormatada = dataHoraNova.toLocaleDateString('pt-BR');
                      
                      const horaOriginal = sol.data_hora_original ? 
                        new Date(sol.data_hora_original).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : 
                        '';
                      const horaNova = dataHoraNova.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                      
                      horaInfo = horaOriginal ? `${horaOriginal} → ${horaNova}` : horaNova;
                      
                      tipoTexto = sol.tipo_marcacao || sol.tipo_ajuste || 'CORREÇÃO';
                    } else {
                      // Ponto Esquecido
                      dataFormatada = sol.data.split('-').reverse().join('/');
                      horaInfo = sol.hora;
                      tipoTexto = sol.tipo_ponto?.replace('_', ' ').toUpperCase() || 'AJUSTE';
                    }
                    
                    return (
                    <Card key={sol.id} className={`border-l-4 ${
                      status === 'aprovado' ? 'border-l-green-500' :
                      status === 'reprovado' || status === 'rejeitado' ? 'border-l-red-500' :
                      'border-l-yellow-500'
                    }`}>
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-lg">
                                {tipoTexto}
                              </p>
                              {isCorrecao && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                  Correção
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">
                              {dataFormatada} {horaInfo && `às ${horaInfo}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {!isCorrecao && status === 'pendente' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditarAjuste(sol)}
                                className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                                title="Editar solicitação"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {status === 'pendente' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSolicitacaoDetalhes(sol)}
                                className="text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                                title="Ver detalhes"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              status === 'aprovado' ? 'bg-green-100 text-green-700' :
                              status === 'reprovado' || status === 'rejeitado' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {status === 'aprovado' ? '✓ Aprovado' :
                               status === 'reprovado' || status === 'rejeitado' ? '✗ Reprovado' :
                               '⏳ Pendente'}
                            </span>
                          </div>
                        </div>

                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-600 mb-1">Justificativa:</p>
                          <p className="bg-gray-50 p-3 rounded text-sm">
                            {sol.justificativa}
                          </p>
                        </div>

                        {sol.observacoes_aprovacao && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-600 mb-1">Observação do RH:</p>
                            <p className="bg-blue-50 border border-blue-200 p-3 rounded text-sm">
                              {sol.observacoes_aprovacao}
                            </p>
                          </div>
                        )}

                        <div className="flex justify-between text-xs text-gray-500 pt-3 border-t">
                          <span>Solicitado em: {new Date(sol.criado_em).toLocaleString('pt-BR')}</span>
                          {sol.data_aprovacao && (
                            <span>Processado em: {new Date(sol.data_aprovacao).toLocaleString('pt-BR')}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Modal de Nova Solicitação */}
          <Dialog open={modalNovaSolicitacao} onOpenChange={(open) => {
            setModalNovaSolicitacao(open);
            if (!open) setEditandoAjusteId(null);
          }}>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Edit className="h-5 w-5 text-purple-600" />
                  Nova Solicitação de Ajuste
                </DialogTitle>
                <DialogDescription>
                  Selecione o tipo de solicitação e preencha os dados necessários
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Seletor de Tipo de Solicitação */}
                <div className="space-y-2">
                  <Label htmlFor="tipo_solicitacao">Tipo de Solicitação *</Label>
                  <Select
                    value={novaSolicitacao.tipo_solicitacao || 'ponto_esquecido'}
                    onValueChange={(value) => setNovaSolicitacao({...novaSolicitacao, tipo_solicitacao: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ponto_esquecido">📍 Ponto Esquecido</SelectItem>
                      <SelectItem value="correcao">✏️ Correção de Registro</SelectItem>
                      <SelectItem value="abono_falta">📋 Abono de Falta</SelectItem>
                      <SelectItem value="banco_horas">⏰ Banco de Horas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Info Box Dinâmico */}
                <div className={`border rounded-lg p-3 ${
                  (novaSolicitacao.tipo_solicitacao || 'ponto_esquecido') === 'ponto_esquecido' ? 'bg-blue-50 border-blue-200' :
                  novaSolicitacao.tipo_solicitacao === 'correcao' ? 'bg-purple-50 border-purple-200' :
                  novaSolicitacao.tipo_solicitacao === 'abono_falta' ? 'bg-orange-50 border-orange-200' :
                  'bg-green-50 border-green-200'
                }`}>
                  <p className={`text-sm ${
                    (novaSolicitacao.tipo_solicitacao || 'ponto_esquecido') === 'ponto_esquecido' ? 'text-blue-800' :
                    novaSolicitacao.tipo_solicitacao === 'correcao' ? 'text-purple-800' :
                    novaSolicitacao.tipo_solicitacao === 'abono_falta' ? 'text-orange-800' :
                    'text-green-800'
                  }`}>
                    <strong>
                      {(novaSolicitacao.tipo_solicitacao || 'ponto_esquecido') === 'ponto_esquecido' && 'Ponto Esquecido:'}
                      {novaSolicitacao.tipo_solicitacao === 'correcao' && 'Correção de Registro:'}
                      {novaSolicitacao.tipo_solicitacao === 'abono_falta' && 'Abono de Falta:'}
                      {novaSolicitacao.tipo_solicitacao === 'banco_horas' && 'Banco de Horas:'}
                    </strong>{' '}
                    {(novaSolicitacao.tipo_solicitacao || 'ponto_esquecido') === 'ponto_esquecido' && 'Use para criar um registro de ponto que você esqueceu de bater.'}
                    {novaSolicitacao.tipo_solicitacao === 'correcao' && 'Corrija a hora de um registro de ponto já existente.'}
                    {novaSolicitacao.tipo_solicitacao === 'abono_falta' && 'Solicite abono para ausências justificadas (atestado, licença, etc).'}
                    {novaSolicitacao.tipo_solicitacao === 'banco_horas' && 'Compense horas negativas usando seu saldo positivo de banco de horas.'}
                  </p>
                </div>

                {/* CAMPOS PONTO ESQUECIDO */}
                {(novaSolicitacao.tipo_solicitacao || 'ponto_esquecido') === 'ponto_esquecido' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="data">Data do Ponto Esquecido *</Label>
                      <Input
                        id="data"
                        type="date"
                        value={novaSolicitacao.data}
                        onChange={(e) => setNovaSolicitacao({...novaSolicitacao, data: e.target.value})}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="hora">Hora Exata *</Label>
                      <Input
                        id="hora"
                        type="time"
                        value={novaSolicitacao.hora}
                        onChange={(e) => setNovaSolicitacao({...novaSolicitacao, hora: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tipo_ponto">Tipo de Ponto *</Label>
                      <Select
                        value={novaSolicitacao.tipo_ponto}
                        onValueChange={(value) => setNovaSolicitacao({...novaSolicitacao, tipo_ponto: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ENTRADA">Entrada</SelectItem>
                          <SelectItem value="SAIDA_INTERVALO">Saída para Intervalo</SelectItem>
                          <SelectItem value="RETORNO_INTERVALO">Retorno do Intervalo</SelectItem>
                          <SelectItem value="SAIDA">Saída</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="justificativa">Justificativa * (mínimo 10 caracteres)</Label>
                      <Textarea
                        id="justificativa"
                        placeholder="Explique por que esqueceu de registrar..."
                        value={novaSolicitacao.justificativa}
                        onChange={(e) => setNovaSolicitacao({...novaSolicitacao, justificativa: e.target.value})}
                        rows={4}
                        className="resize-none"
                      />
                      <p className="text-xs text-gray-500">
                        {novaSolicitacao.justificativa?.length || 0} caracteres
                      </p>
                    </div>
                  </>
                )}

                {/* CAMPOS CORREÇÃO DE REGISTRO - NOVO FLUXO EM 4 PASSOS */}
                {novaSolicitacao.tipo_solicitacao === 'correcao' && (
                  <>
                    {/* Passo 1: Selecionar Data */}
                    <div className="space-y-2">
                      <Label htmlFor="data_registro">1. Selecione a Data do Registro *</Label>
                      <Input
                        id="data_registro"
                        type="date"
                        value={novaSolicitacao.data}
                        onChange={(e) => {
                          setNovaSolicitacao({...novaSolicitacao, data: e.target.value, hora: ''});
                          setRegistroSelecionado(null);
                          if (e.target.value) {
                            buscarRegistrosParaCorrigir(e.target.value);
                          } else {
                            setRegistrosParaCorrigir([]);
                          }
                        }}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    {/* Passo 2: Selecionar Registro */}
                    {novaSolicitacao.data && (
                      <div className="space-y-2">
                        <Label>2. Selecione o Registro que Deseja Corrigir *</Label>
                        
                        {buscandoRegistros ? (
                          <div className="p-4 text-center text-gray-500">
                            Buscando registros...
                          </div>
                        ) : registrosParaCorrigir.length === 0 ? (
                          <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg border">
                            Nenhum registro encontrado nesta data
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {registrosParaCorrigir.map((registro) => {
                              const dataHora = new Date(registro.data_hora);
                              const hora = dataHora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                              const tipoTexto = {
                                'ENTRADA': 'Entrada',
                                'SAIDA_INTERVALO': 'Saída para Intervalo',
                                'RETORNO_INTERVALO': 'Retorno do Intervalo',
                                'SAIDA': 'Saída'
                              }[registro.tipo_marcacao] || registro.tipo_marcacao;
                              
                              return (
                                <Button
                                  key={registro.id}
                                  type="button"
                                  variant={registroSelecionado?.id === registro.id ? "default" : "outline"}
                                  className="w-full justify-between h-auto py-3"
                                  onClick={() => {
                                    setRegistroSelecionado(registro);
                                    setNovaSolicitacao({...novaSolicitacao, hora: ''});
                                  }}
                                >
                                  <div className="text-left">
                                    <span className="font-bold text-lg">{hora}</span>
                                    <span className="ml-2">{tipoTexto}</span>
                                  </div>
                                  {registroSelecionado?.id === registro.id && (
                                    <span>✓ Selecionado</span>
                                  )}
                                </Button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Passo 3: Digitar Hora Correta */}
                    {registroSelecionado && (
                      <div className="space-y-2">
                        <Label htmlFor="hora_correta">3. Digite a Hora Correta *</Label>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
                          <p className="text-sm text-yellow-800">
                            <strong>Hora Atual:</strong> {new Date(registroSelecionado.data_hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            <br />
                            <strong>Digite abaixo a hora correta:</strong>
                          </p>
                        </div>
                        <Input
                          id="hora_correta"
                          type="time"
                          value={novaSolicitacao.hora}
                          onChange={(e) => setNovaSolicitacao({...novaSolicitacao, hora: e.target.value})}
                          placeholder="Ex: 09:00"
                        />
                      </div>
                    )}

                    {/* Passo 4: Justificativa */}
                    {registroSelecionado && novaSolicitacao.hora && (
                      <div className="space-y-2">
                        <Label htmlFor="justificativa_correcao">4. Justificativa * (mínimo 10 caracteres)</Label>
                        <Textarea
                          id="justificativa_correcao"
                          placeholder="Explique por que o horário precisa ser corrigido..."
                          value={novaSolicitacao.justificativa}
                          onChange={(e) => setNovaSolicitacao({...novaSolicitacao, justificativa: e.target.value})}
                          rows={4}
                          className="resize-none"
                        />
                        <p className="text-xs text-gray-500">
                          {novaSolicitacao.justificativa?.length || 0} caracteres
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* CAMPOS ABONO DE FALTA */}
                {novaSolicitacao.tipo_solicitacao === 'abono_falta' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="data_inicio">Data Início *</Label>
                      <Input
                        id="data_inicio"
                        type="date"
                        value={novaSolicitacao.data}
                        onChange={(e) => setNovaSolicitacao({...novaSolicitacao, data: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="data_fim">Data Fim *</Label>
                      <Input
                        id="data_fim"
                        type="date"
                        value={novaSolicitacao.data_fim || novaSolicitacao.data}
                        onChange={(e) => setNovaSolicitacao({...novaSolicitacao, data_fim: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Período do Abono (deixe vazio para dia(s) inteiro(s))</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="hora_inicio_abono" className="text-xs">Das (HH:MM)</Label>
                          <Input
                            id="hora_inicio_abono"
                            type="time"
                            value={novaSolicitacao.hora_inicio || ''}
                            onChange={(e) => setNovaSolicitacao({...novaSolicitacao, hora_inicio: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="hora_fim_abono" className="text-xs">Até (HH:MM)</Label>
                          <Input
                            id="hora_fim_abono"
                            type="time"
                            value={novaSolicitacao.hora_fim || ''}
                            onChange={(e) => setNovaSolicitacao({...novaSolicitacao, hora_fim: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="justificativa_tipo">Tipo de Justificativa *</Label>
                      <Select
                        value={novaSolicitacao.justificativa_falta_id || ''}
                        onValueChange={(value) => setNovaSolicitacao({...novaSolicitacao, justificativa_falta_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="atestado">Atestado Médico</SelectItem>
                          <SelectItem value="licenca">Licença</SelectItem>
                          <SelectItem value="outro">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="motivo">Motivo/Observações *</Label>
                      <Textarea
                        id="motivo"
                        placeholder="Descreva o motivo da ausência..."
                        value={novaSolicitacao.justificativa}
                        onChange={(e) => setNovaSolicitacao({...novaSolicitacao, justificativa: e.target.value})}
                        rows={4}
                        className="resize-none"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Anexar Documento (opcional)</Label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-orange-500 transition-colors">
                        <input
                          type="file"
                          id="file-upload-abono"
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setNovaSolicitacao({...novaSolicitacao, anexo: file});
                            }
                          }}
                          className="hidden"
                        />
                        <label htmlFor="file-upload-abono" className="cursor-pointer">
                          {novaSolicitacao.anexo ? (
                            <div>
                              <FileCheck className="h-8 w-8 mx-auto text-green-500 mb-1" />
                              <p className="text-sm font-medium text-gray-900">{novaSolicitacao.anexo.name}</p>
                              <p className="text-xs text-gray-500">{(novaSolicitacao.anexo.size / 1024).toFixed(2)} KB</p>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setNovaSolicitacao({...novaSolicitacao, anexo: null});
                                }}
                                className="text-xs text-red-600 mt-1 underline"
                              >
                                Remover
                              </button>
                            </div>
                          ) : (
                            <div>
                              <Upload className="h-8 w-8 mx-auto text-gray-400 mb-1" />
                              <p className="text-sm text-gray-600">Clique para anexar atestado ou comprovante</p>
                              <p className="text-xs text-gray-500">PDF, JPG, PNG, DOC ou DOCX</p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                  </>
                )}

                {/* CAMPOS BANCO DE HORAS */}
                {novaSolicitacao.tipo_solicitacao === 'banco_horas' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="data_compensacao">Data a Compensar *</Label>
                      <Input
                        id="data_compensacao"
                        type="date"
                        value={novaSolicitacao.data}
                        onChange={(e) => setNovaSolicitacao({...novaSolicitacao, data: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Período a Compensar *</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="hora_inicio_banco" className="text-xs">Das (HH:MM)</Label>
                          <Input
                            id="hora_inicio_banco"
                            type="time"
                            value={novaSolicitacao.hora_inicio || ''}
                            onChange={(e) => setNovaSolicitacao({...novaSolicitacao, hora_inicio: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="hora_fim_banco" className="text-xs">Até (HH:MM)</Label>
                          <Input
                            id="hora_fim_banco"
                            type="time"
                            value={novaSolicitacao.hora_fim || ''}
                            onChange={(e) => setNovaSolicitacao({...novaSolicitacao, hora_fim: e.target.value})}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="motivo_banco">Motivo *</Label>
                      <Textarea
                        id="motivo_banco"
                        placeholder="Explique o motivo da compensação..."
                        value={novaSolicitacao.justificativa}
                        onChange={(e) => setNovaSolicitacao({...novaSolicitacao, justificativa: e.target.value})}
                        rows={4}
                        className="resize-none"
                      />
                    </div>
                  </>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setModalNovaSolicitacao(false);
                    setNovaSolicitacao({
                      tipo_solicitacao: 'ponto_esquecido',
                      tipo_ponto: 'ENTRADA',
                      data: '',
                      data_fim: '',
                      hora: '',
                      hora_inicio: '',
                      hora_fim: '',
                      horas_compensar: '',
                      justificativa_falta_id: '',
                      justificativa: '',
                      anexo: null
                    });
                    setRegistrosParaCorrigir([]);
                    setRegistroSelecionado(null);
                  }}
                  disabled={enviandoSolicitacao}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleEnviarSolicitacao}
                  disabled={enviandoSolicitacao}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {enviandoSolicitacao ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Enviar Solicitação
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal de Detalhes da Solicitação */}
          <Dialog open={!!solicitacaoDetalhes} onOpenChange={(open) => { if (!open) setSolicitacaoDetalhes(null); }}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-purple-600" />
                  Detalhes da Solicitação
                </DialogTitle>
              </DialogHeader>

              {solicitacaoDetalhes && (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Tipo</Label>
                      <p className="font-medium">
                        {solicitacaoDetalhes.tipo_solicitacao === 'correcao'
                          ? solicitacaoDetalhes.tipo_marcacao || solicitacaoDetalhes.tipo_ajuste || 'CORREÇÃO'
                          : solicitacaoDetalhes.tipo_ponto?.replace('_', ' ').toUpperCase() || 'AJUSTE'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Status</Label>
                      <p>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium inline-block ${
                          solicitacaoDetalhes.status_aprovacao === 'APROVADO' || solicitacaoDetalhes.status === 'aprovado'
                            ? 'bg-green-100 text-green-700'
                            : solicitacaoDetalhes.status_aprovacao === 'REJEITADO' || solicitacaoDetalhes.status === 'reprovado'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {solicitacaoDetalhes.status_aprovacao === 'APROVADO' || solicitacaoDetalhes.status === 'aprovado'
                            ? 'Aprovado'
                            : solicitacaoDetalhes.status_aprovacao === 'REJEITADO' || solicitacaoDetalhes.status === 'reprovado'
                            ? 'Reprovado'
                            : 'Pendente'}
                        </span>
                      </p>
                    </div>
                  </div>

                  {solicitacaoDetalhes.tipo_solicitacao === 'correcao' ? (
                    <div>
                      <Label className="text-xs text-gray-500">Tipo de Solicitação</Label>
                      <p className="text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded inline-block mt-1">
                        Correção
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs text-gray-500">Tipo de Solicitação</Label>
                      <p className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded inline-block mt-1">
                        Ponto Esquecido
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Data</Label>
                      <p className="font-medium">
                        {solicitacaoDetalhes.tipo_solicitacao === 'correcao'
                          ? new Date(solicitacaoDetalhes.data_hora_nova).toLocaleDateString('pt-BR')
                          : solicitacaoDetalhes.data.split('-').reverse().join('/')}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Horário</Label>
                      <p className="font-medium">
                        {solicitacaoDetalhes.tipo_solicitacao === 'correcao' ? (
                          <>
                            {solicitacaoDetalhes.data_hora_original
                              ? `${new Date(solicitacaoDetalhes.data_hora_original).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} → ${new Date(solicitacaoDetalhes.data_hora_nova).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                              : new Date(solicitacaoDetalhes.data_hora_nova).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </>
                        ) : (
                          solicitacaoDetalhes.hora
                        )}
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-gray-500">Justificativa</Label>
                    <p className="bg-gray-50 p-3 rounded text-sm mt-1">
                      {solicitacaoDetalhes.justificativa}
                    </p>
                  </div>

                  {solicitacaoDetalhes.observacoes_aprovacao && (
                    <div>
                      <Label className="text-xs text-gray-500">Observação do RH</Label>
                      <p className="bg-blue-50 border border-blue-200 p-3 rounded text-sm mt-1">
                        {solicitacaoDetalhes.observacoes_aprovacao}
                      </p>
                    </div>
                  )}

                  <div className="text-xs text-gray-500 pt-3 border-t">
                    <p>Solicitado em: {new Date(solicitacaoDetalhes.criado_em).toLocaleString('pt-BR')}</p>
                    {solicitacaoDetalhes.data_aprovacao && (
                      <p className="mt-1">Processado em: {new Date(solicitacaoDetalhes.data_aprovacao).toLocaleString('pt-BR')}</p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setSolicitacaoDetalhes(null)}
                    >
                      Fechar
                    </Button>
                    {(solicitacaoDetalhes.status_aprovacao === 'PENDENTE' || solicitacaoDetalhes.status === 'pendente') && (
                      <Button
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleExcluirSolicitacao(solicitacaoDetalhes)}
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Cancelar Solicitação
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>


        {/* Aba Folha de Ponto */}
        {allowedTabs.includes('folha-ponto') && (
        <TabsContent value="folha-ponto">
          <FolhaPonto 
            funcionarioId={user?.employee_id}
            userEmail={user?.email}
            showPasswordChangeModal={showPasswordChangeModal}
            setShowPasswordChangeModal={setShowPasswordChangeModal}
            allowDeleteRegistro={allowDeleteRegistro}
          />
        </TabsContent>
        )}

        {/* Aba Enviar Documento */}
        <TabsContent value="enviar-documento">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Formulário de Envio */}
            <Card>
              <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Enviar Novo Documento
                </CardTitle>
                <CardDescription className="text-orange-100">
                  Atestados, comprovantes e outros documentos
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {/* Tipo de Documento */}
                <div>
                  <Label htmlFor="tipo_doc">Tipo de Documento *</Label>
                  <Select
                    value={documentoForm.tipo_documento_id}
                    onValueChange={(value) => setDocumentoForm({...documentoForm, tipo_documento_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposDocumentos.map((tipo) => (
                        <SelectItem key={tipo.id} value={tipo.id}>
                          {tipo.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Motivo */}
                <div>
                  <Label htmlFor="motivo_doc">Motivo / Observação *</Label>
                  <Textarea
                    id="motivo_doc"
                    value={documentoForm.motivo}
                    onChange={(e) => setDocumentoForm({...documentoForm, motivo: e.target.value})}
                    placeholder="Explique brevemente o motivo do envio..."
                    rows={4}
                  />
                </div>

                {/* Upload */}
                <div>
                  <Label>Arquivo *</Label>
                  <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-500 transition-colors">
                    <input
                      type="file"
                      id="file-upload-web"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setDocumentoForm({...documentoForm, arquivo: file});
                        }
                      }}
                      className="hidden"
                    />
                    <label htmlFor="file-upload-web" className="cursor-pointer">
                      {documentoForm.arquivo ? (
                        <div>
                          <FileCheck className="h-12 w-12 mx-auto text-green-500 mb-2" />
                          <p className="font-semibold text-gray-900">{documentoForm.arquivo.name}</p>
                          <p className="text-sm text-gray-500">{(documentoForm.arquivo.size / 1024).toFixed(2)} KB</p>
                          <p className="text-xs text-orange-600 mt-2">Clique para alterar</p>
                        </div>
                      ) : (
                        <div>
                          <Upload className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-600 mb-1">Clique para selecionar</p>
                          <p className="text-xs text-gray-500">PDF, JPG, PNG, DOC ou DOCX</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <Button
                  onClick={handleEnviarDocumentoWeb}
                  disabled={enviandoDocumento}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  {enviandoDocumento ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Enviar Documento
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Lista de Documentos Enviados */}
            <Card>
              <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Meus Documentos
                </CardTitle>
                <CardDescription className="text-blue-100">
                  Histórico de documentos enviados
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {carregandoDocumentos ? (
                  <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 mx-auto animate-spin text-blue-600 mb-4" />
                    <p className="text-gray-600">Carregando...</p>
                  </div>
                ) : meusDocumentos.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">Nenhum documento enviado</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {meusDocumentos.map((doc) => (
                      <Card key={doc.id} className="border border-gray-200">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{doc.tipo_documento_nome}</p>
                              <p className="text-sm text-gray-600 mt-1">{doc.motivo}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              doc.status === 'pendente' ? 'bg-yellow-100 text-yellow-700' :
                              doc.status === 'visualizado' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {doc.status === 'pendente' ? 'Pendente' :
                               doc.status === 'visualizado' ? 'Visualizado' :
                               doc.status}
                            </span>
                          </div>
                          
                          <div className="text-xs text-gray-500 space-y-1">
                            <p>📄 {doc.arquivo_nome}</p>
                            <p>📅 {new Date(doc.criado_em).toLocaleString('pt-BR')}</p>
                            {doc.visualizado_em && (
                              <p>👁️ Visualizado em {new Date(doc.visualizado_em).toLocaleString('pt-BR')}</p>
                            )}
                          </div>

                          {doc.observacoes_rh && (
                            <div className="mt-2 bg-blue-50 border border-blue-200 p-2 rounded text-xs">
                              <p className="font-semibold text-blue-900">Observação do RH:</p>
                              <p className="text-blue-800">{doc.observacoes_rh}</p>
                            </div>
                          )}

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadDocumento(doc.id, doc.arquivo_nome)}
                            className="w-full mt-3"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Baixar
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Aba Documentos Assinados */}
        {allowedTabs.includes('documentos') && (
        <TabsContent value="documentos">
          <DocumentosAssinados 
            funcionarioId={user?.employee_id}
          />
        </TabsContent>
        )}

        {/* Aba Relatórios */}
        {allowedTabs.includes('relatorios') && (
        <TabsContent value="relatorios">
          <RelatoriosPonto 
            funcionarioId={user?.employee_id}
          />
        </TabsContent>
        )}

        {/* Aba Configurações */}
        {allowedTabs.includes('configuracoes') && (
        <TabsContent value="configuracoes">
          <ConfiguracoesPonto 
            isAdmin={user?.is_admin}
          />
        </TabsContent>
        )}
      </Tabs>
      
      {/* Modal de Webcam para Selfie */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Registrar Ponto - {tipoSelecionado?.replace(/_/g, ' ')}</span>
                <Button variant="outline" size="sm" onClick={fecharModal}>
                  <XCircle className="h-4 w-4" />
                </Button>
              </CardTitle>
              <CardDescription>Tire uma foto para confirmar o registro</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!fotoCapturada ? (
                <>
                  <div className="relative bg-black rounded-lg overflow-hidden">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-96 object-cover"
                    />
                  </div>
                  <Button
                    onClick={capturarFoto}
                    disabled={capturandoFoto}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    📸 Capturar Foto
                  </Button>
                </>
              ) : (
                <>
                  <div className="relative bg-black rounded-lg overflow-hidden">
                    <img
                      src={fotoCapturada}
                      alt="Selfie capturada"
                      className="w-full h-96 object-cover"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => {
                        setFotoCapturada(null);
                        startCamera();
                      }}
                      variant="outline"
                    >
                      🔄 Tirar Novamente
                    </Button>
                    <Button
                      onClick={registrarPontoComFoto}
                      disabled={registrandoPonto}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {registrandoPonto ? 'Registrando...' : '✓ Confirmar Registro'}
                    </Button>
                  </div>
                </>
              )}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </CardContent>
          </Card>
        </div>
      )}

      {/* NOVO: Modal do Mapa GPS */}
      {showMapModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Map className="h-6 w-6" />
                <h2 className="text-xl font-bold">Sua Localização GPS</h2>
              </div>
              <button
                onClick={() => setShowMapModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-semibold text-gray-700">Latitude:</span>
                    <div className="font-mono text-blue-600">{location?.latitude?.toFixed(6) || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Longitude:</span>
                    <div className="font-mono text-blue-600">{location?.longitude?.toFixed(6) || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Precisão:</span>
                    <div className={`font-semibold ${
                      location?.accuracy < 20 ? 'text-green-600' :
                      location?.accuracy < 50 ? 'text-yellow-600' :
                      'text-orange-600'
                    }`}>
                      ±{location?.accuracy?.toFixed(0) || 'N/A'} metros
                    </div>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Leituras:</span>
                    <div className="text-blue-600">
                      {location?.fromMultipleReadings ? `${location.numReadings} leituras` : '1 leitura'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <iframe
                  width="100%"
                  height="300"
                  frameBorder="0"
                  style={{ border: 0 }}
                  src={location?.latitude && location?.longitude ? 
                    `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${location.latitude},${location.longitude}&zoom=18` : 
                    ''}
                  allowFullScreen
                  title="Mapa de Localização"
                />
              </div>

              <div className="mt-4">
                <a
                  href={location?.latitude && location?.longitude ? 
                    `https://www.google.com/maps?q=${location.latitude},${location.longitude}` : 
                    '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-blue-600 text-white rounded-lg py-3 font-semibold hover:bg-blue-700 transition-colors"
                >
                  Abrir no Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para Histórico de Registros
const HistoricoRegistros = ({ funcionarioId, onSolicitarAjuste }) => {
  const [registros, setRegistros] = useState([]);
  const [filtroData, setFiltroData] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    carregarHistorico();
  }, [funcionarioId, filtroData]);

  const carregarHistorico = async () => {
    if (!funcionarioId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/api/timetracking/registros/${funcionarioId}`;
      
      if (filtroData) {
        const dataInicio = new Date(filtroData).toISOString();
        const dataFim = new Date(new Date(filtroData).getTime() + 24 * 60 * 60 * 1000).toISOString();
        url += `?data_inicio=${dataInicio}&data_fim=${dataFim}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRegistros(response.data.registros);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar histórico",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4">
            <div className="flex-1">
              <Label htmlFor="filtro-data">Data</Label>
              <Input
                id="filtro-data"
                type="date"
                value={filtroData}
                onChange={(e) => setFiltroData(e.target.value)}
              />
            </div>
            <Button onClick={carregarHistorico} className="mt-6">
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Registros */}
      <Card>
        <CardHeader>
          <CardTitle>Registros de Ponto</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Activity className="h-8 w-8 mx-auto animate-spin text-blue-600" />
              <p className="text-gray-600 mt-2">Carregando registros...</p>
            </div>
          ) : registros.length > 0 ? (
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {registros.map((registro) => (
                <div key={registro.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div>
                      <div className="font-medium">
                        {new Date(registro.data_hora).toLocaleDateString('pt-BR')}
                      </div>
                      <div className="text-sm text-gray-600">
                        {new Date(registro.data_hora).toLocaleTimeString('pt-BR')}
                      </div>
                    </div>
                    <div>{getTipoMarcacaoBadge(registro.tipo_marcacao)}</div>
                    <div className="text-sm text-gray-600">{registro.origem}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {registro.geofence_valida ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    {getStatusBadge(registro.status)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nenhum registro encontrado para o filtro selecionado</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Componente para Relatórios
const RelatoriosPonto = ({ funcionarioId }) => {
  const [espelhoPonto, setEspelhoPonto] = useState(null);
  const [arquivoAEJ, setArquivoAEJ] = useState(null);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const gerarEspelhoPonto = async () => {
    if (!funcionarioId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/espelho/${funcionarioId}?mes=${mes}&ano=${ano}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEspelhoPonto(response.data);
      toast({
        title: "Sucesso!",
        description: "Espelho de ponto gerado com sucesso!",
        variant: "default"
      });
    } catch (error) {
      console.error('Erro ao gerar espelho:', error);
      toast({
        title: "Erro",
        description: "Erro ao gerar espelho de ponto",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const gerarAEJ = async () => {
    if (!funcionarioId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/aej/${funcionarioId}?mes=${mes}&ano=${ano}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setArquivoAEJ(response.data);
      toast({
        title: "Sucesso!",
        description: "Arquivo AEJ gerado com sucesso!",
        variant: "default"
      });
    } catch (error) {
      console.error('Erro ao gerar AEJ:', error);
      toast({
        title: "Erro",
        description: "Erro ao gerar arquivo AEJ",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const baixarAEJ = () => {
    if (!arquivoAEJ) return;
    
    const blob = new Blob([arquivoAEJ.arquivo_aej], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AEJ_${arquivoAEJ.funcionario.nome}_${arquivoAEJ.periodo}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-6">
      {/* Controles */}
      <Card>
        <CardHeader>
          <CardTitle>Gerar Relatórios</CardTitle>
          <CardDescription>
            Selecione o período e gere os relatórios obrigatórios
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4 items-end">
            <div>
              <Label htmlFor="mes">Mês</Label>
              <select
                id="mes"
                value={mes}
                onChange={(e) => setMes(parseInt(e.target.value))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2025, i, 1).toLocaleDateString('pt-BR', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="ano">Ano</Label>
              <Input
                id="ano"
                type="number"
                value={ano}
                onChange={(e) => setAno(parseInt(e.target.value))}
                min="2020"
                max="2030"
              />
            </div>
            <Button onClick={gerarEspelhoPonto} disabled={loading}>
              <FileText className="h-4 w-4 mr-2" />
              Espelho de Ponto
            </Button>
            <Button onClick={gerarAEJ} disabled={loading} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Gerar AEJ
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Espelho de Ponto */}
      {espelhoPonto && (
        <Card>
          <CardHeader>
            <CardTitle>Espelho de Ponto - {espelhoPonto.periodo.descricao}</CardTitle>
            <CardDescription>
              {espelhoPonto.funcionario.nome} - {espelhoPonto.funcionario.cargo}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{espelhoPonto.resumo.total_dias_trabalhados}</div>
                <div className="text-sm text-gray-600">Dias Trabalhados</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{espelhoPonto.resumo.total_horas_mes}h</div>
                <div className="text-sm text-gray-600">Total de Horas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{espelhoPonto.resumo.total_horas_extras}h</div>
                <div className="text-sm text-gray-600">Horas Extras</div>
              </div>
            </div>
            
            <div className="text-xs text-gray-500">
              Gerado em: {new Date(espelhoPonto.gerado_em).toLocaleString('pt-BR')}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Arquivo AEJ */}
      {arquivoAEJ && (
        <Card>
          <CardHeader>
            <CardTitle>Arquivo Eletrônico de Jornada (AEJ)</CardTitle>
            <CardDescription>
              Arquivo gerado conforme Portaria 671/2021
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-medium">Total de Registros:</div>
                  <div>{arquivoAEJ.total_registros}</div>
                </div>
                <div>
                  <div className="font-medium">Total de Ajustes:</div>
                  <div>{arquivoAEJ.total_ajustes}</div>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="font-medium mb-2">Hash de Integridade:</div>
                <code className="text-xs bg-gray-100 p-2 rounded block">
                  {arquivoAEJ.hash_arquivo}
                </code>
              </div>
              
              <Button onClick={baixarAEJ} className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Baixar Arquivo AEJ
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Componente para Configurações (Admin)
const ConfiguracoesPonto = ({ isAdmin }) => {
  const [geofences, setGeofences] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [ajustesPendentes, setAjustesPendentes] = useState([]);
  const [novaGeofence, setNovaGeofence] = useState({
    empresa_id: '1',
    nome: '',
    latitude: '',
    longitude: '',
    raio_metros: 100,
    ativo: true
  });
  const { toast } = useToast();

  useEffect(() => {
    if (isAdmin) {
      carregarConfiguracoes();
    }
  }, [isAdmin]);

  const carregarConfiguracoes = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Carregar geofences
      const geofencesRes = await axios.get(`${API}/api/timetracking/geofences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeofences(geofencesRes.data);

      // Carregar horários
      const horariosRes = await axios.get(`${API}/api/timetracking/horarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHorarios(horariosRes.data);

      // Carregar ajustes pendentes
      const ajustesRes = await axios.get(`${API}/api/timetracking/ajustes/pendentes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAjustesPendentes(ajustesRes.data);
      
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">Acesso restrito a administradores</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Ajustes Pendentes */}
      {ajustesPendentes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <span>Ajustes Pendentes ({ajustesPendentes.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {ajustesPendentes.slice(0, 5).map((ajuste) => (
                <div key={ajuste.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div>
                    <div className="font-medium">{ajuste.funcionario_nome}</div>
                    <div className="text-sm text-gray-600">{ajuste.justificativa}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(ajuste.criado_em).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                  <Badge variant="secondary">Pendente</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Geofences */}
      <Card>
        <CardHeader>
          <CardTitle>Geofences Configuradas</CardTitle>
          <CardDescription>
            {geofences.length} área(s) de trabalho configurada(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {geofences.map((geo) => (
              <div key={geo.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium">{geo.nome}</div>
                  <div className="text-sm text-gray-600">
                    Raio: {geo.raio_metros}m | Lat: {geo.latitude}, Lng: {geo.longitude}
                  </div>
                </div>
                <Badge variant={geo.ativo ? 'default' : 'secondary'}>
                  {geo.ativo ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Horários de Trabalho */}
      <Card>
        <CardHeader>
          <CardTitle>Horários de Trabalho</CardTitle>
          <CardDescription>
            {horarios.length} horário(s) configurado(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {horarios.map((horario) => (
              <div key={horario.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium">{horario.nome}</div>
                  <div className="text-sm text-gray-600">
                    {horario.entrada} às {horario.saida}
                    {horario.intervalo_inicio && ` | Intervalo: ${horario.intervalo_inicio} - ${horario.intervalo_fim}`}
                  </div>
                  <div className="text-xs text-gray-500">
                    Carga: {horario.carga_horaria_diaria}h | Tolerância: {horario.tolerancia_minutos}min
                  </div>
                </div>
                <Badge variant={horario.ativo ? 'default' : 'secondary'}>
                  {horario.ativo ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// ========== COMPONENTE DOCUMENTOS ASSINADOS ==========
const DocumentosAssinados = ({ funcionarioId }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [documentos, setDocumentos] = useState([]);

  useEffect(() => {
    carregarDocumentos();
  }, []);

  const carregarDocumentos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/listar-espelhos-assinados`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('Documentos recebidos:', response.data);
      setDocumentos(response.data.documentos || []);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || 'Erro ao carregar documentos',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const baixarPDF = async (documento) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/download-pdf-espelho/${documento.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Criar URL do blob e fazer download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `folha_ponto_${documento.funcionario_nome}_${documento.periodo_inicio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Sucesso!",
        description: "PDF baixado com sucesso",
        variant: "default"
      });
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      toast({
        title: "Erro",
        description: "Erro ao baixar PDF",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center space-x-2">
                <Download className="h-6 w-6 text-blue-600" />
                <span>Documentos Assinados</span>
              </CardTitle>
              <CardDescription className="text-base mt-1">
                Histórico de Folhas de Ponto assinadas eletronicamente
              </CardDescription>
            </div>
            <Button
              onClick={carregarDocumentos}
              variant="outline"
              size="sm"
              disabled={loading}
            >
              {loading ? 'Atualizando...' : '🔄 Atualizar'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && documentos.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Carregando documentos...</p>
            </div>
          ) : documentos.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Nenhum Documento Assinado
              </h3>
              <p className="text-gray-600">
                Você ainda não possui folhas de ponto assinadas.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {documentos.map((doc) => (
                <Card key={doc.id} className="border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Badge className="bg-green-600 text-white">
                            ✓ Assinado
                          </Badge>
                          <span className="text-sm text-gray-600">
                            {new Date(doc.data_assinatura).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Folha de Ponto - {(() => {
                            // CORREÇÃO: Evitar conversão de timezone
                            const [ano, mes] = doc.periodo_inicio.split('-');
                            const meses = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 
                                          'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
                            return `${meses[parseInt(mes) - 1]} de ${ano}`;
                          })()}
                        </h3>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mt-2">
                          <div>
                            <span className="font-medium">Período:</span>{' '}
                            {(() => {
                              // CORREÇÃO: Evitar conversão de timezone
                              const [anoI, mesI, diaI] = doc.periodo_inicio.split('-');
                              const [anoF, mesF, diaF] = doc.periodo_fim.split('-');
                              return `${diaI}/${mesI}/${anoI} a ${diaF}/${mesF}/${anoF}`;
                            })()}
                          </div>
                          <div>
                            <span className="font-medium">Total Trabalhadas:</span>{' '}
                            {doc.total_horas_trabalhadas}
                          </div>
                          <div className="col-span-2">
                            <span className="font-medium">Empresa:</span> {doc.empresa_nome}
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => baixarPDF(doc)}
                        className="ml-4 bg-blue-600 hover:bg-blue-700"
                        size="lg"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Baixar PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// ========== COMPONENTE FOLHA DE PONTO ==========
const FolhaPonto = ({ funcionarioId, userEmail, showPasswordChangeModal, setShowPasswordChangeModal, allowDeleteRegistro }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [tipoPeriodo, setTipoPeriodo] = useState('mensal'); // mensal ou customizado
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(2025); // Fixo por enquanto para debug
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [espelho, setEspelho] = useState(null);
  const [diaExpandido, setDiaExpandido] = useState(null);
  const [registrosDetalhados, setRegistrosDetalhados] = useState({});
  const [modalAssinatura, setModalAssinatura] = useState(false);
  const [senha, setSenha] = useState('');
  const [capturandoSelfie, setCapturandoSelfie] = useState(false);
  const [selfieCapturada, setSelfieCapturada] = useState(null);
  const videoRefAssinatura = useRef(null);
  const canvasRefAssinatura = useRef(null);
  const streamRefAssinatura = useRef(null);


  const gerarEspelho = async () => {
    console.log('🔵 gerarEspelho chamado');
    console.log('👤 funcionarioId:', funcionarioId);
    console.log('📅 tipoPeriodo:', tipoPeriodo);
    
    if (!funcionarioId) {
      console.log('❌ Funcionário não identificado');
      toast({
        title: "Erro",
        description: "Funcionário não identificado",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    console.log('⏳ Loading iniciado');
    
    try {
      const token = localStorage.getItem('token');
      let periodo_inicio, periodo_fim;
      
      if (tipoPeriodo === 'mensal') {
        // Garantir que mes e ano sejam números válidos
        const mesNum = parseInt(mes, 10);
        const anoNum = parseInt(ano, 10);
        
        if (isNaN(mesNum) || isNaN(anoNum)) {
          console.log('❌ Mês ou ano inválido');
          toast({
            title: "Erro",
            description: "Mês ou ano inválido",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
        
        // Calcular primeiro e último dia do mês
        periodo_inicio = `${anoNum}-${String(mesNum).padStart(2, '0')}-01`;
        const ultimoDia = new Date(anoNum, mesNum, 0).getDate();
        periodo_fim = `${anoNum}-${String(mesNum).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
        
        console.log(`📅 Período calculado: ${periodo_inicio} a ${periodo_fim}`);
      } else {
        if (!dataInicio || !dataFim) {
          console.log('❌ Datas não preenchidas');
          toast({
            title: "Erro",
            description: "Selecione as datas de início e fim",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
        periodo_inicio = dataInicio;
        periodo_fim = dataFim;
        console.log(`📅 Período customizado: ${periodo_inicio} a ${periodo_fim}`);
      }

      console.log('🔑 Token:', token ? 'presente' : 'ausente');
      console.log('📡 Fazendo requisição POST...');

      const response = await axios.post(
        `${API}/api/timetracking/gerar-espelho`,
        {
          funcionario_id: funcionarioId,
          periodo_inicio: periodo_inicio,
          periodo_fim: periodo_fim,
          tipo_periodo: tipoPeriodo
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('✅ Resposta recebida:', response.data);
      setEspelho(response.data);
      toast({
        title: "Sucesso!",
        description: "Espelho de ponto gerado com sucesso",
        variant: "default"
      });
    } catch (error) {
      console.error('❌ Erro ao gerar espelho:', error);
      console.error('❌ Detalhes:', error.response?.data);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || 'Erro ao gerar espelho de ponto',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      console.log('⏳ Loading finalizado');
    }
  };


  // Buscar registros detalhados de um dia
  const buscarRegistrosDia = async (data, forcarAtualizacao = false) => {
    if (registrosDetalhados[data] && !forcarAtualizacao) {
      // Já tem os dados, apenas toggle
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/registros/${funcionarioId}`,
        {
          params: {
            data_inicio: data,
            data_fim: data
          },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        }
      );

      setRegistrosDetalhados(prev => ({
        ...prev,
        [data]: response.data.registros || []
      }));
    } catch (error) {
      console.error('Erro ao buscar registros:', error);
      setRegistrosDetalhados(prev => ({ ...prev, [data]: [] }));
      toast({
        title: "Erro",
        description: "Erro ao buscar registros do dia",
        variant: "destructive"
      });
    }
  };

  // Deletar um registro específico
  const deletarRegistro = async (registroId, data) => {
    if (!confirm('Tem certeza que deseja deletar este registro?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/api/timetracking/registro/${registroId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: "Sucesso",
        description: "Registro deletado com sucesso"
      });

      // Forçar atualização dos registros do dia
      await buscarRegistrosDia(data, true);
      
      // Regerar o espelho para atualizar totalizadores
      await gerarEspelho();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast({
        title: "Erro",
        description: "Erro ao deletar registro",
        variant: "destructive"
      });
    }
  };

  // Toggle expandir dia
  const toggleDiaExpandido = async (data) => {
    if (diaExpandido === data) {
      setDiaExpandido(null);
    } else {
      setDiaExpandido(data);
      await buscarRegistrosDia(data);
    }
  };
  const abrirModalAssinatura = () => {
    if (!espelho) {
      toast({
        title: "Erro",
        description: "Gere o espelho antes de assinar",
        variant: "destructive"
      });
      return;
    }
    if (espelho.assinado) {
      toast({
        title: "Já Assinado",
        description: "Este espelho já foi assinado anteriormente",
        variant: "destructive"
      });
      return;
    }
    setModalAssinatura(true);
    setSenha('');
    setSelfieCapturada(null);
    iniciarCameraAssinatura();
  };

  const iniciarCameraAssinatura = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      if (videoRefAssinatura.current) {
        videoRefAssinatura.current.srcObject = stream;
      }
      streamRefAssinatura.current = stream;
      setCapturandoSelfie(true);
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      toast({
        title: "Erro",
        description: "Erro ao acessar câmera",
        variant: "destructive"
      });
    }
  };

  const capturarSelfieAssinatura = () => {
    const video = videoRefAssinatura.current;
    const canvas = canvasRefAssinatura.current;
    
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imagemBase64 = canvas.toDataURL('image/jpeg');
      setSelfieCapturada(imagemBase64);
      
      // Parar stream
      if (streamRefAssinatura.current) {
        streamRefAssinatura.current.getTracks().forEach(track => track.stop());
      }
      setCapturandoSelfie(false);
    }
  };

  const fecharModalAssinatura = () => {
    if (streamRefAssinatura.current) {
      streamRefAssinatura.current.getTracks().forEach(track => track.stop());
    }
    setModalAssinatura(false);
    setSenha('');
    setSelfieCapturada(null);
    setCapturandoSelfie(false);
  };

  const assinarEspelho = async () => {
    if (!senha) {
      toast({
        title: "Erro",
        description: "Digite sua senha",
        variant: "destructive"
      });
      return;
    }
    if (!selfieCapturada) {
      toast({
        title: "Erro",
        description: "Capture a selfie antes de assinar",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/timetracking/assinar-espelho`,
        {
          espelho_id: espelho.id,
          senha: senha,
          selfie_base64: selfieCapturada
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: "✅ Assinado com Sucesso!",
        description: "Seu espelho de ponto foi assinado eletronicamente. O PDF está disponível para download.",
        variant: "default"
      });
      
      fecharModalAssinatura();
      
      // Atualizar espelho com informações da resposta
      setEspelho({
        ...espelho,
        assinado: true,
        data_assinatura: response.data.assinado_em || new Date().toISOString(),
        pdf_url: response.data.pdf_url
      });
    } catch (error) {
      console.error('Erro ao assinar espelho:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao assinar espelho';
      toast({
        title: "Erro na Assinatura",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const baixarPDFAssinado = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/download-pdf-espelho/${espelho.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Criar URL do blob e fazer download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `folha_ponto_${espelho.funcionario_nome}_${espelho.periodo_inicio}_${espelho.periodo_fim}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Sucesso!",
        description: "PDF baixado com sucesso",
        variant: "default"
      });
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      toast({
        title: "Erro",
        description: "Erro ao baixar PDF",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Card de Seleção de Período */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Folha de Ponto</span>
          </CardTitle>
          <CardDescription>
            Visualize e assine eletronicamente sua folha de ponto mensal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tipo de Período */}
          <div className="space-y-2">
            <Label>Tipo de Período</Label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  value="mensal"
                  checked={tipoPeriodo === 'mensal'}
                  onChange={(e) => setTipoPeriodo(e.target.value)}
                />
                <span>Mensal</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  value="customizado"
                  checked={tipoPeriodo === 'customizado'}
                  onChange={(e) => setTipoPeriodo(e.target.value)}
                />
                <span>Período Customizado</span>
              </label>
            </div>
          </div>

          {/* Seleção Mensal */}
          {tipoPeriodo === 'mensal' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mês</Label>
                <select
                  value={mes}
                  onChange={(e) => setMes(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2000, i).toLocaleString('pt-BR', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Ano</Label>
                <Input
                  type="number"
                  value={ano}
                  onChange={(e) => {
                    const valor = parseInt(e.target.value, 10);
                    if (!isNaN(valor)) {
                      setAno(valor);
                    }
                  }}
                  min={2020}
                  max={2030}
                />
              </div>
            </div>
          )}

          {/* Seleção Customizada */}
          {tipoPeriodo === 'customizado' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                />
              </div>
            </div>
          )}

          <Button
            onClick={gerarEspelho}
            disabled={loading}
            className="w-full"
          >
            {loading ? 'Gerando...' : 'Gerar Folha de Ponto'}
          </Button>
        </CardContent>
      </Card>

      {/* Visualização do Espelho */}
      {!espelho && !loading && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="text-center space-y-3 py-8">
              <FileText className="h-16 w-16 mx-auto text-blue-400" />
              <h3 className="text-lg font-semibold text-gray-700">
                Nenhum Espelho de Ponto Gerado
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Selecione um período acima e clique em "Gerar Folha de Ponto" para visualizar seus registros e totalizadores.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {espelho && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-2xl">FOLHA DE PONTO</CardTitle>
                <CardDescription className="text-base mt-1">
                  Período: {(() => {
                    // CORREÇÃO CRÍTICA: Evitar conversão de timezone usando split
                    const [anoI, mesI, diaI] = espelho.periodo_inicio.split('-');
                    const [anoF, mesF, diaF] = espelho.periodo_fim.split('-');
                    return `${diaI}/${mesI}/${anoI} a ${diaF}/${mesF}/${anoF}`;
                  })()}
                </CardDescription>
              </div>
              {espelho.assinado && (
                <Badge className="bg-green-600 text-white px-3 py-1 text-sm">
                  ✓ Assinado Eletronicamente
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Dados do Empregador */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-2">DADOS DO EMPREGADOR</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><strong>Razão Social:</strong> {espelho.empresa_nome}</div>
                <div><strong>CNPJ:</strong> {espelho.empresa_cnpj}</div>
                <div className="col-span-2"><strong>Endereço:</strong> {espelho.empresa_endereco}</div>
              </div>
            </div>

            {/* Dados do Colaborador */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-2">DADOS DO COLABORADOR</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><strong>Nome:</strong> {espelho.funcionario_nome}</div>
                <div><strong>CPF:</strong> {espelho.funcionario_cpf}</div>
                <div><strong>PIS:</strong> {espelho.funcionario_pis}</div>
                <div><strong>CTPS:</strong> {espelho.funcionario_ctps}</div>
                <div><strong>Cargo:</strong> {espelho.funcionario_cargo}</div>
                <div><strong>Admissão:</strong> {espelho.funcionario_admissao ? new Date(espelho.funcionario_admissao).toLocaleDateString('pt-BR') : 'N/A'}</div>
              </div>
            </div>

            {/* Jornada Padrão */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-2">QUADRO DE HORÁRIOS</h3>
              <div className="text-sm">
                <strong>Jornada:</strong> {espelho.jornada_padrao}
              </div>
            </div>

            {/* Tabela de Registros Diários */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse border">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border px-2 py-2">DIA/MÊS</th>
                    <th className="border px-2 py-2">PONTOS</th>
                    <th className="border px-2 py-2">TRABALHADAS</th>
                    <th className="border px-2 py-2">ABONO</th>
                    <th className="border px-2 py-2">SALDO</th>
                    <th className="border px-2 py-2 w-24">AÇÕES</th>
                  </tr>
                </thead>
                <tbody>
                  {espelho.registros_diarios.map((registro, idx) => {
                    // CORREÇÃO CRÍTICA: Evitar conversão de timezone usando split ao invés de new Date()
                    const [ano, mes, dia] = registro.data?.split('-') || ['', '', ''];
                    const dataFormatada = dia && mes ? `${dia}/${mes}` : registro.data;
                    const numMarcacoes = (registro.marcacoes || []).length;
                    const registrosDoDia = registrosDetalhados[registro.data] || [];
                    
                    return (
                    <React.Fragment key={idx}>
                      <tr className={registro.dia_semana === 'Sábado' || registro.dia_semana === 'Domingo' ? 'bg-gray-50' : ''}>
                        <td className="border px-2 py-1">
                          {dataFormatada} {registro.dia_semana}
                        </td>
                        <td className="border px-2 py-1">
                          {registro.marcacoes.length > 0 ? registro.marcacoes.join(' | ') : '-'}
                          {numMarcacoes > 4 && (
                            <span className="ml-2 text-xs text-red-600 font-bold">
                              ⚠️ {numMarcacoes} marcações!
                            </span>
                          )}
                        </td>
                        <td className="border px-2 py-1 text-center">{registro.horas_trabalhadas}</td>
                        <td className="border px-2 py-1 text-center">{registro.abono_previstas || '-'}</td>
                        <td className="border px-2 py-1 text-center">{registro.saldo_dia}</td>
                        <td className="border px-2 py-1 text-center">
                          {numMarcacoes > 0 && (
                            <button
                              onClick={() => toggleDiaExpandido(registro.data)}
                              className="text-blue-600 hover:text-blue-800 text-xs"
                            >
                              {diaExpandido === registro.data ? '▼ Fechar' : '▶ Editar'}
                            </button>
                          )}
                        </td>
                      </tr>
                      
                      {/* Linha expandida com detalhes dos registros */}
                      {diaExpandido === registro.data && (
                        <tr>
                          <td colSpan="6" className="border bg-blue-50 px-4 py-3">
                            <div className="text-sm">
                              <div className="font-semibold mb-2">Registros de {dataFormatada}:</div>
                              {registrosDoDia.length > 0 ? (
                                <div className="space-y-1">
                                  {registrosDoDia.map((reg, regIdx) => (
                                    <div key={regIdx} className="flex items-center justify-between bg-white px-3 py-2 rounded border">
                                      <div className="flex items-center space-x-3">
                                        <span className="font-mono">{reg.data_hora?.substring(11, 16) || '-'}</span>
                                        <span className="text-gray-600">{reg.tipo_marcacao || 'Registro'}</span>
                                      </div>
                                      {allowDeleteRegistro && (
                                        <button
                                          onClick={() => deletarRegistro(reg.id, registro.data)}
                                          className="text-red-600 hover:text-red-800 text-xs px-2 py-1 border border-red-300 rounded hover:bg-red-50"
                                          data-testid="delete-registro-ponto-btn"
                                        >
                                          🗑️ Deletar
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-gray-500">Carregando registros...</div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totalizadores */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">TOTALIZADORES</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="border rounded p-2">
                  <div className="text-gray-600">Total Trabalhadas</div>
                  <div className="text-lg font-bold">{espelho.totalizadores.total_horas_trabalhadas}</div>
                </div>
                <div className="border rounded p-2">
                  <div className="text-gray-600">Horas Extras</div>
                  <div className="text-lg font-bold">{espelho.totalizadores.total_horas_extras_diurnas}</div>
                </div>
                <div className="border rounded p-2">
                  <div className="text-gray-600">Atrasos</div>
                  <div className="text-lg font-bold">{espelho.totalizadores.total_atrasos}</div>
                </div>
                <div className="border rounded p-2">
                  <div className="text-gray-600">Dias Faltosos</div>
                  <div className="text-lg font-bold">{espelho.totalizadores.dias_faltosos}</div>
                </div>
                <div className="border rounded p-2">
                  <div className="text-gray-600">Faltas (horas)</div>
                  <div className="text-lg font-bold">{espelho.totalizadores.faltas_em_horas}</div>
                </div>
                <div className="border rounded p-2">
                  <div className="text-gray-600">Saldo Anterior</div>
                  <div className="text-lg font-bold">{espelho.totalizadores.saldo_anterior_banco_horas}</div>
                </div>
                <div className="border rounded p-2 col-span-2">
                  <div className="text-gray-600">Saldo Acumulado Banco de Horas</div>
                  <div className="text-lg font-bold text-blue-600">{espelho.totalizadores.saldo_acumulado_banco_horas}</div>
                </div>
              </div>
            </div>

            {/* Botão de Assinatura */}
            {!espelho.assinado && (
              <div className="pt-4 border-t">
                <Button
                  onClick={abrirModalAssinatura}
                  className="w-full bg-green-600 hover:bg-green-700"
                  size="lg"
                >
                  ✍️ Assinar Eletronicamente
                </Button>
              </div>
            )}

            {/* Informação de Assinatura */}
            {espelho.assinado && espelho.data_assinatura && (
              <div className="pt-4 border-t bg-green-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center space-x-2 text-green-800">
                  <CheckCircle className="h-5 w-5" />
                  <div>
                    <div className="font-semibold">Documento Assinado</div>
                    <div className="text-sm">
                      Assinado em: {new Date(espelho.data_assinatura).toLocaleString('pt-BR')}
                    </div>
                  </div>
                </div>
                
                {/* Botão Download PDF */}
                <Button
                  onClick={baixarPDFAssinado}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  size="lg"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Baixar PDF Assinado
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal: Alteração de Senha Obrigatória */}
      <Dialog open={showPasswordChangeModal} onOpenChange={setShowPasswordChangeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              Alteração de Senha Obrigatória
            </DialogTitle>
            <DialogDescription>
              <div className="flex items-start gap-2 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  Para registrar o ponto, você precisa alterar sua senha. 
                  Após alterar, faça login novamente e tente registrar o ponto.
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowPasswordChangeModal(false)}
            >
              Cancelar
            </Button>
            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              onClick={() => {
                logout();
                window.location.href = '/login';
              }}
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Alterar Senha
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Assinatura */}
      {modalAssinatura && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Assinar Folha de Ponto</span>
                <Button variant="outline" size="sm" onClick={fecharModalAssinatura}>
                  <XCircle className="h-4 w-4" />
                </Button>
              </CardTitle>
              <CardDescription>
                Para assinar, digite sua senha e tire uma selfie
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Campo de Senha */}
              <div className="space-y-2">
                <Label>Senha</Label>
                <Input
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="Digite sua senha"
                />
              </div>

              {/* Captura de Selfie */}
              <div className="space-y-2">
                <Label>Selfie de Confirmação (Obrigatória)</Label>
                {!selfieCapturada ? (
                  <>
                    <div className="relative bg-black rounded-lg overflow-hidden">
                      <video
                        ref={videoRefAssinatura}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-64 object-cover"
                      />
                    </div>
                    <Button
                      onClick={capturarSelfieAssinatura}
                      disabled={!capturandoSelfie}
                      className="w-full"
                    >
                      📸 Capturar Selfie
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="relative bg-black rounded-lg overflow-hidden">
                      <img
                        src={selfieCapturada}
                        alt="Selfie"
                        className="w-full h-64 object-cover"
                      />
                    </div>
                    <Button
                      onClick={() => {
                        setSelfieCapturada(null);
                        iniciarCameraAssinatura();
                      }}
                      variant="outline"
                      className="w-full"
                    >
                      🔄 Tirar Nova Foto
                    </Button>
                  </>
                )}
              </div>

              {/* Canvas oculto para captura */}
              <canvas ref={canvasRefAssinatura} style={{ display: 'none' }} />

              {/* Botão Confirmar Assinatura */}
              <Button
                onClick={assinarEspelho}
                disabled={loading || !senha || !selfieCapturada}
                className="w-full bg-green-600 hover:bg-green-700"
                size="lg"
              >
                {loading ? 'Assinando...' : '✍️ Confirmar Assinatura'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TimeTrackingPage;
